/* eslint-disable jsx-a11y/anchor-is-valid */
import { AnimatePresence, motion } from "framer-motion";
import {
  Archive,
  Building,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  CircleIcon,
  Clock,
  Edit2,
  Eye,
  Inbox,
  Mail,
  MapPin,
  Phone,
  Plus,
  Search,
  Star,
  Trash2,
  TrendingUp,
  Users,
  X,
  UserPlus,
  PhoneCall,
  UserCheck,
  UserX,
  UserCog,
  Award,
  UserMinus,
  User,
  Mic,
  List,
  LayoutGrid,
  Home,
  FileText,
  Upload,
  Download,
  Check,
  Grid,
  SlidersHorizontal,
  RefreshCw,
  StickyNote,
  EyeOff,
  RotateCcw
} from "lucide-react";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { normalizePhoneInput } from "../../../utils/phoneNumber";
import "react-phone-number-input/style.css";
import { useSelector } from "react-redux";
import api from "../../../api";
import { getAgents, getProperties, registerProperty, updateProperty } from "../../../api/agent";
import { addLead, addLeadToPropertyEnquiry, bulkIgnoreLeads, deleteLead, getLeads, ignoreLead, updateLead, getLeadReminders, createLeadReminders, deleteLeadReminder } from "../../../api/lead";
import { capitalizeEachWord, cleanLeadMessageText, formatOfferSubmissionSummary } from "../../../helper/function";
import { AddPropertyForm } from "../../properties/pages/AddPropertyPage";
import ReactPaginate from "react-paginate";
import { getSpecificCrawlerProperty } from "../../../api/crawler";
import PageHeader from "../../../components/shared/PageHeader";
import { apiCache, cacheKeys, cachedApiCall, invalidateCache } from '../../../lib/cache';
import DeleteConfirmationModal from "../../../components/common/DeleteConfirmationModal";
import { PipelineBoard } from "../../../components/pipeline/PipelineBoard";
import PropertyMatchingResults from "../../../components/properties/PropertyMatchingResults";
import * as documentsAPI from "../../../api/documents";
import LeadPreviewModal from "../../../components/leads/LeadPreviewModal";
import OfferWorkflowActions from "../../offers/components/OfferWorkflowActions";
import BuyerPreferencesModal from "../../contacts/components/BuyerPreferencesModal";
import RentingPreferencesModal from "../../contacts/components/RentingPreferencesModal";
import ContactLink from "../../contacts/components/ContactLink";
import IgnoreLeadReasonModal from "../components/IgnoreLeadReasonModal";
import LeadNotesModal from "../components/LeadNotesModal";
import ReassignIgnoredLeadModal from "../components/ReassignIgnoredLeadModal";
import { hasAdminAccess, hasSuperAdminAccess } from "../../../utils/roleAccess";
import AgencyScopeSwitch, {
  useAgencyScope,
  useExtraAgents,
  IncludeAgentsPicker,
} from "../../../components/common/AgencyScopeSwitch";

const sourceLabels = {
  realestate: "realestate.com.au",
  form: "Form",
  buyer_portal: "Buyer Portal",
  direct: "Direct Contact",
  voiceCaller: "Voice Caller",
  chatbot: "ChatBot",
  referral: "Referral",
  social: "Social Media",
  website: "Website",
  other: "Other Sources",
};

const timelineLabels = {
  immediate: "Immediate (0–30 days)",
  short: "Short-term (1–3 months)",
  medium: "Medium-term (3–6 months)",
  long: "Long-term (6-12 months)",
  very_long: "Very long-term (12+ months)",
};

// The selling time frame ("When are you thinking of selling?") is captured
// verbatim from the website form onto lead.timeline, so its wording varies
// (e.g. "6-12 Months"). Offer the common choices for editing; whatever the lead
// actually said is always shown too (see mergeTimeframeOptions).
const SELLING_TIMEFRAME_OPTIONS = [
  "Immediate",
  "0-3 Months",
  "3-6 Months",
  "6-12 Months",
  "12+ Months",
  "Just researching",
];

const mergeTimeframeOptions = (current) =>
  current && !SELLING_TIMEFRAME_OPTIONS.includes(current)
    ? [current, ...SELLING_TIMEFRAME_OPTIONS]
    : SELLING_TIMEFRAME_OPTIONS;

const allStages = [
  { id: "Unassessed", name: "Unassessed" },
  { id: "New Lead", name: "New Lead" },
  { id: "Attempting Contact", name: "Attempting Contact" },
  { id: "Contact - Not Qualified", name: "Contact - Not Qualified" },
  { id: "Contacted Nurture", name: "Contacted Nurture" },
  { id: "Booked Appraisal", name: "Booked Appraisal" },
  {
    id: "Appraisal Complete - Awaiting Decision",
    name: "Appraisal Complete - Awaiting Decision",
  },
  { id: "Follow-Up Ongoing", name: "Follow-Up Ongoing" },
  { id: "Won - Listing Signed", name: "Won - Listing Signed" },
  {
    id: "Lost - Listed With Another Agent",
    name: "Lost - Listed With Another Agent",
  },
  { id: "Unresponsive - Cold", name: "Unresponsive - Cold" },
];

// Row action buttons in the Buyer Enquiries table. Two solid buttons for the
// decisions (View, Add to Property / Restore), quiet outlined ones for the rest,
// laid out on a fixed 3-column grid so nothing wraps onto a stray line
// (Michael Piccoli, Monday 13283958708).
const ROW_BTN = "h-8 px-2.5 text-xs font-semibold rounded-lg inline-flex items-center justify-center gap-1.5 whitespace-nowrap transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#55C5D0]/40 [&>svg]:shrink-0";
const ROW_BTN_QUIET = `${ROW_BTN} bg-white border`;

const cleanEnquiryMessage = (msg) => {
  if (msg?.includes("[Buyer Portal Offer Submission]")) {
    return formatOfferSubmissionSummary(msg);
  }
  return cleanLeadMessageText(msg);
};

const LeadManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);
  // Only a super admin can re-assign an ignored lead (#12984261525 revision).
  const canReassignIgnored = hasSuperAdminAccess(user);
  const [reassignLead, setReassignLead] = useState(null);

  // WHOSE ENQUIRIES. This page used to hand every admin the entire office,
  // because the backend decided visibility with a bare `role != 'admin'` test.
  // Simon Abbott, 3 Sep 2026: "I've still got other agents inquiries, leads,
  // appraisals etc in my CRM. Please ensure I only have mine and Gennevas so I
  // can start to sort through it all." So the default is now your own desk,
  // with an opt-in switch to the office and a picker for the one or two
  // colleagues you actually work alongside. Agents never see either control -
  // they have only ever had their own rows.
  const {
    agencyWide,
    setAgencyWide,
    canSwitch: canScope,
    canIncludeAgents,
  } = useAgencyScope("enquiries");
  const { agentIds: extraAgentIds, setAgentIds: setExtraAgentIds, toggleAgentId } =
    useExtraAgents("enquiries", canIncludeAgents);

  // What the scope means to the API. Sent explicitly rather than left to the
  // server default, so a PA or the principal who has chosen "My records" gets
  // their own rows instead of the role default they never asked for.
  const scopeParams = React.useMemo(() => {
    if (!canScope && !canIncludeAgents) return {};
    if (agencyWide) return { agency_wide: "true" };
    return extraAgentIds.length
      ? { agency_wide: "false", agents: extraAgentIds.join(",") }
      : { agency_wide: "false" };
  }, [canScope, canIncludeAgents, agencyWide, extraAgentIds]);

  // One primitive for the fetch effect's dependency list - an object literal
  // would be a new reference on every render and refetch forever.
  const scopeKey = React.useMemo(
    () =>
      `${canScope ? 1 : 0}${canIncludeAgents ? 1 : 0}:${agencyWide ? 1 : 0}:${[...extraAgentIds]
        .sort()
        .join(",")}`,
    [canScope, canIncludeAgents, agencyWide, extraAgentIds]
  );
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [property, setProperty] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [lead, setLead] = useState([]);
  const [editLead, setEditLead] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [leadsList, setLeadsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPropertyMatches, setShowPropertyMatches] = useState(false);
  const debounce = useRef(null);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [settlementDateDropdownOpen, setSettlementDateDropdownOpen] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedSettlementDate, setSelectedSettlementDate] = useState(null);
  const [showProcessed, setShowProcessed] = useState(false);
  
  // Badge counts derived from leadsList
  const { enquiryCount, offerCount, inspectionCount, renterCount, applicationCount, totalLeads } = React.useMemo(() => {
    const enquiries = leadsList.filter(l => l.lead_type !== 'offer' && l.lead_type !== 'inspection' && l.lead_type !== 'renter_enquiry' && l.lead_type !== 'agent_application');
    const offers = leadsList.filter(l => l.lead_type === 'offer');
    const inspections = leadsList.filter(l => l.lead_type === 'inspection');
    const renters = leadsList.filter(l => l.lead_type === 'renter_enquiry');
    // Agent applications: people answering a recruitment ad, never customers.
    const applications = leadsList.filter(l => l.lead_type === 'agent_application');

    const getUniqueCount = (list) => {
      const groups = new Set();
      list.forEach(lead => {
        const email = (lead.email || '').toLowerCase().trim();
        let phone = (lead.contact || lead.phone || '').replace(/\D/g, '');
        if (phone.startsWith('61')) phone = '0' + phone.substring(2);
        const property = (lead.property_address || '').toLowerCase().trim();
        const contactId = phone || email || lead.id;
        groups.add(`${contactId}|${property}`);
      });
      return groups.size;
    };

    return {
      enquiryCount: getUniqueCount(enquiries),
      offerCount: getUniqueCount(offers),
      inspectionCount: getUniqueCount(inspections),
      renterCount: getUniqueCount(renters),
      applicationCount: getUniqueCount(applications),
      totalLeads: getUniqueCount(leadsList)
    };
  }, [leadsList]);
  const filterRef = useRef(null);
  const sourceRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const settlementDateRef = useRef(null);
  const [analytics, setAnalytics] = useState({});
  const [phoneError, setPhoneError] = useState("");
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);

  // Vault-style holding area state — initialise from URL ?tab= param
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'offers') return 'offers';
    if (tab === 'inspections') return 'inspections';
    if (tab === 'renters') return 'renters';
    if (tab === 'applications') return 'applications';
    if (tab === 'ignored') return 'ignored';
    return 'enquiries';
  };
  // 'enquiries' | 'offers' | 'inspections' | 'renters' | 'applications' | 'ignored'
  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Sync tab with URL query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const param = params.get('tab');
    const tab = param === 'offers' ? 'offers'
      : param === 'inspections' ? 'inspections'
        : param === 'renters' ? 'renters'
          : param === 'applications' ? 'applications'
            : param === 'ignored' ? 'ignored'
              : 'enquiries';
    // Agent Applications is admin-only. Typing the URL gets you the Buyer
    // Enquiries tab instead; the API returns nothing for a non-admin either.
    const allowed = tab === 'applications' && !isAdmin ? 'enquiries' : tab;
    if (allowed !== activeTab) {
      setActiveTab(allowed);
    }
  }, [location.search, activeTab, isAdmin]);

  // Ignored rows live in their own list. Ignore marks the lead, it never deletes
  // it, so everything cleared out of the holding area can be brought back
  // (Christian Gravias, Monday 12838026421).
  const [ignoredList, setIgnoredList] = useState([]);
  const [ignoredLoading, setIgnoredLoading] = useState(false);
  const [restoringLeadId, setRestoringLeadId] = useState(null);

  const [assigningLeadId, setAssigningLeadId] = useState(null);
  const [ignoringLeadId, setIgnoringLeadId] = useState(null);
  const [bulkIgnoring, setBulkIgnoring] = useState(false);
  const [bulkIgnoreOpen, setBulkIgnoreOpen] = useState(false); // "Ignore Selected" waiting on the "why ignore?" modal
  const [assignPropertyModal, setAssignPropertyModal] = useState({ open: false, lead: null });
  // Buying-preferences editor opened from the enquiry/inspection view modal — the
  // lead may not have a resolved contact_id yet, so BuyerPreferencesModal resolves
  // (get-or-creates) a Contact from whatever identity fields are on hand.
  const [prefsLead, setPrefsLead] = useState(null);
  // The enquiry the Notes + reminder series window is open for. Christian
  // Gravias, Monday 13166757620: "Add a notes button which also includes
  // reminder series to the buyer enquiries tab". Same window as Seller Leads.
  const [notesLead, setNotesLead] = useState(null);
  const [ignoreGroup, setIgnoreGroup] = useState(null); // group pending the "why ignore?" modal
  const [assignPropertyId, setAssignPropertyId] = useState('');
  const [assignProperties, setAssignProperties] = useState([]);
  const [assignPropertiesLoading, setAssignPropertiesLoading] = useState(false);
  const [assignMessage, setAssignMessage] = useState('');
  const [expandedMessages, setExpandedMessages] = useState({});

  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [voiceAgents, setVoiceAgents] = useState([]);
  const [selectedVoiceAgent, setSelectedVoiceAgent] = useState("");
  const [isCalling, setIsCalling] = useState(false);

  // Document management state
  const [leadDocuments, setLeadDocuments] = useState([]);
  const [loadingLeadDocs, setLoadingLeadDocs] = useState(false);
  const [uploadingLeadDoc, setUploadingLeadDoc] = useState(false);
  const [selectedLeadFile, setSelectedLeadFile] = useState(null);
  const [showDocDeleteModal, setShowDocDeleteModal] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  // Client-side pagination of the rows shown for the active tab, so no tab is a
  // long scroll. Page size is user-selectable (default 10). JP, CRM-2026-00097.
  const [pageSize, setPageSize] = useState(10);
  const [uiPage, setUiPage] = useState(1);
  const [selectedLeads, setSelectedLeads] = useState([]);

  // Filter dropdown state - Type/Date/Property/Agent are derived from
  // whatever's already loaded for the active tab (client-side); Source reuses
  // the existing selectedSource state, which already flows into the server
  // fetch (see fetchLeads / the useEffect below) - it just never had a UI
  // control (John Paul, 2026-08-07).
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('all');
  const [selectedDateFilter, setSelectedDateFilter] = useState('all');
  const [selectedPropertyFilter, setSelectedPropertyFilter] = useState('all');
  const [selectedAgentFilter, setSelectedAgentFilter] = useState('all');

  // Which recruitment ad an application came off. lead_ingest writes the form /
  // campaign block into initial_notes and also stamps the form name onto tags,
  // so read the note first and fall back to the tag.
  const applicationCampaign = (lead) => {
    const notes = lead?.initial_notes || '';
    const match = notes.match(/^\s*-\s*(?:Form|Campaign|Ad):\s*(.+)$/m);
    if (match) return match[1].trim();
    const tags = Array.isArray(lead?.tags) ? lead.tags : [];
    return tags.find((t) => t && t !== 'Facebook Lead') || '';
  };

  const rowTypeLabel = (lead) => {
    if (lead.lead_type === 'offer') return 'Offer';
    if (lead.lead_type === 'inspection') return 'Inspection Request';
    if (lead.lead_type === 'renter_enquiry') return 'Renter Enquiry';
    if (lead.lead_type === 'agent_application') return 'Agent Application';
    return lead.source === 'form' ? 'Appraisal Lead' : 'Buyer Enquiry';
  };

  const DATE_RANGE_OPTIONS = [
    { id: 'all', label: 'All time' },
    { id: 'today', label: 'Today' },
    { id: 'last_7_days', label: 'Last 7 days' },
    { id: 'last_30_days', label: 'Last 30 days' },
    { id: 'last_90_days', label: 'Last 90 days' },
  ];

  // 1. Filter by Tab (include on_market/processed leads when toggle is on) -
  // split out from the rest of the pipeline so the filter dropdowns below can
  // derive their options (Type/Property/Agent) from the tab's full data, not
  // from an already-filtered subset.
  const tabLeads = React.useMemo(() => (
    activeTab === 'ignored'
      ? ignoredList
      : activeTab === 'enquiries'
        ? leadsList.filter(l => l.lead_type !== 'offer' && l.lead_type !== 'inspection' && l.lead_type !== 'renter_enquiry' && l.lead_type !== 'agent_application' && (l.lead_type !== 'on_market' || l.is_processed))
        : activeTab === 'offers'
          ? leadsList.filter(l => l.lead_type === 'offer')
          : activeTab === 'renters'
            ? leadsList.filter(l => l.lead_type === 'renter_enquiry')
            : activeTab === 'applications'
              ? leadsList.filter(l => l.lead_type === 'agent_application')
              : leadsList.filter(l => l.lead_type === 'inspection')
  ), [leadsList, ignoredList, activeTab]);

  const typeFilterOptions = React.useMemo(
    () => Array.from(new Set(tabLeads.map(rowTypeLabel))).sort(),
    [tabLeads]
  );
  const propertyFilterOptions = React.useMemo(
    () => Array.from(new Set(tabLeads.map(l => l.property_address).filter(Boolean))).sort(),
    [tabLeads]
  );
  // Michael Piccoli (DOMA 13261735033): "always allow us to toggle between every
  // agent even if they don't currently have any enquiries/leads." So the dropdown
  // lists every agent (from the agents list), unioned with any agent_name already on
  // a loaded row — which keeps "N/A" (unassigned) and any name not in the agent list.
  // `agents` must be declared ABOVE this memo: reading it before its useState line
  // threw "Cannot access 'agents' before initialization" and blanked /leads.
  const [agents, setAgents] = useState([]);
  const agentFilterOptions = React.useMemo(
    () => Array.from(new Set([
      ...agents.map(a => a.name).filter(Boolean),
      ...tabLeads.map(l => l.agent_name).filter(Boolean),
    ])).sort(),
    [agents, tabLeads]
  );

  // Centralized Grouping Logic
  const groupedLeadsData = React.useMemo(() => {
    // 2. Filter by Search
    let filtered = searchTerm
      ? tabLeads.filter(l => {
        const q = searchTerm.toLowerCase();
        return (
          (l.full_name || l.name || '').toLowerCase().includes(q) ||
          (l.email || '').toLowerCase().includes(q) ||
          (l.contact || l.phone || '').toLowerCase().includes(q) ||
          (l.property_address || '').toLowerCase().includes(q)
        );
      })
      : tabLeads;

    // 2b. Type / Property / Agent filters
    if (selectedTypeFilter !== 'all') {
      filtered = filtered.filter(l => rowTypeLabel(l) === selectedTypeFilter);
    }
    if (selectedPropertyFilter !== 'all') {
      filtered = filtered.filter(l => l.property_address === selectedPropertyFilter);
    }
    if (selectedAgentFilter !== 'all') {
      filtered = filtered.filter(l => l.agent_name === selectedAgentFilter);
    }

    // 2c. Date received filter
    if (selectedDateFilter !== 'all') {
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const cutoffs = { today: startOfToday, last_7_days: 7, last_30_days: 30, last_90_days: 90 };
      const cutoff = cutoffs[selectedDateFilter];
      const since = cutoff instanceof Date ? cutoff : new Date(now.getTime() - cutoff * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(l => l.created_at && new Date(l.created_at) >= since);
    }

    const filteredBySearch = filtered;

    // 3. Group by Contact + Property
    const groups = {};
    filteredBySearch.forEach((lead) => {
      const email = (lead.email || '').toLowerCase().trim();
      let phone = (lead.contact || lead.phone || '').replace(/\D/g, '');
      if (phone.startsWith('61')) phone = '0' + phone.substring(2);
      const property = (lead.property_address || '').toLowerCase().trim();
      const contactId = phone || email || lead.id; // fallback to ID if no contact info
      const key = `${contactId}|${property}`;

      if (!groups[key]) groups[key] = [];
      groups[key].push(lead);
    });

    const groupEntries = Object.values(groups).sort((a, b) => {
      return new Date(b[0].created_at) - new Date(a[0].created_at);
    });

    return { filteredLeads: filteredBySearch, groupEntries };
  }, [tabLeads, searchTerm, selectedTypeFilter, selectedPropertyFilter, selectedAgentFilter, selectedDateFilter]);

  // Column customization state
  const STORAGE_KEY = 'collings:lms:leadsPage:visibleColumns:v1';
  const allColumns = [
    { id: 'received', label: 'Received', alwaysOn: true },
    { id: 'name', label: 'Name', alwaysOn: true },
    { id: 'phone', label: 'Phone Number' },
    { id: 'email', label: 'Email' },
    { id: 'message', label: 'Message' },
    { id: 'property', label: 'Property' },
    { id: 'source', label: 'Source' },
    { id: 'agent', label: 'Assigned Agent' },
    { id: 'priority', label: 'Priority' },
    { id: 'stage', label: 'Stage' },
    { id: 'budget', label: 'Budget' },
    { id: 'actions', label: 'Actions', alwaysOn: true },
  ];

  const defaultVisible = [
    'received',
    'name',
    'phone',
    'email',
    'message',
    'property',
    'source',
    'agent',
    'actions',
  ];

  const [visibleColumns, setVisibleColumns] = useState(defaultVisible);
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      const enforced = new Set(parsed);
      allColumns.filter(c => c.alwaysOn).forEach(c => enforced.add(c.id));
      setVisibleColumns(Array.from(enforced));
    } catch (_) { }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns));
    } catch (_) { }
  }, [visibleColumns]);

  useEffect(() => {
    if (!showColumnMenu) return;
    const onDocMouseDown = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [showColumnMenu]);

  const isVisible = (id) => visibleColumns.includes(id);

  const toggleColumn = (id) => {
    const col = allColumns.find(c => c.id === id);
    if (col?.alwaysOn) return;
    setVisibleColumns((prev) => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      return [...prev, id];
    });
  };

  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [prevPageUrl, setPrevPageUrl] = useState(null);

  // Agents state (declared above agentFilterOptions)
  useEffect(() => {
    const fetchAgentsList = async () => {
      try {
        const response = await getAgents();
        const agentsList = response.data?.results || response.data || [];
        setAgents(agentsList);
      } catch (error) {
        console.error("Failed to fetch agents:", error);
      }
    };
    fetchAgentsList();
  }, []);

  // View mode state (table or kanban)
  const [viewMode, setViewMode] = useState("table");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);

  // Fetch leads list – Buyer Enquiries page shows buyer_enquiry + offer types
  const fetchLeads = async (
    data = { query: "", source: "", priority: "", stage: "", settlement_date: "", includeProcessed: false },
    page = 1,
    forceRefresh = false
  ) => {
    setError(null);

    try {
      // Scope rides on every tab's request, including the ones fired by a
      // delete / assign / ignore handler that calls fetchLeads() with no
      // arguments at all - so it goes in here, not at the call sites.
      const baseParams = { ...data, ...scopeParams, page, page_size: 100 };
      const enquiryParams = { ...baseParams, type: 'buyer_enquiry' };
      const offerParams = { ...baseParams, type: 'offer' };
      const inspectionParams = { ...baseParams, type: 'inspection' };
      const processedParams = { ...baseParams, type: 'on_market' };
      const renterParams = { ...baseParams, type: 'renter_enquiry' };
      const applicationParams = { ...baseParams, type: 'agent_application' };

      if (!apiCache.has(cacheKeys.leads(enquiryParams)) || forceRefresh) {
        setLoading(true);
      }

      // Fetch types in parallel (include processed if toggle is on)
      const fetchPromises = [
        cachedApiCall(
          cacheKeys.leads(enquiryParams),
          () => getLeads(enquiryParams),
          2 * 60 * 1000,
          { forceRefresh }
        ),
        cachedApiCall(
          cacheKeys.leads(offerParams),
          () => getLeads(offerParams),
          2 * 60 * 1000,
          { forceRefresh }
        ),
        cachedApiCall(
          cacheKeys.leads(inspectionParams),
          () => getLeads(inspectionParams),
          2 * 60 * 1000,
          { forceRefresh }
        ),
        cachedApiCall(
          cacheKeys.leads(renterParams),
          () => getLeads(renterParams),
          2 * 60 * 1000,
          { forceRefresh }
        ),
      ];

      // Add processed (on_market) leads if toggle is on
      if (data.includeProcessed) {
        fetchPromises.push(
          cachedApiCall(
            cacheKeys.leads(processedParams),
            () => getLeads(processedParams),
            2 * 60 * 1000,
            { forceRefresh }
          )
        );
      }

      // Agent applications ride alongside rather than inside fetchPromises so
      // the positional destructure below (which already has one optional entry)
      // stays readable. Non-admins never ask for them - the API would return an
      // empty page anyway, so this saves a pointless round trip.
      const applicationPromise = isAdmin
        ? cachedApiCall(
          cacheKeys.leads(applicationParams),
          () => getLeads(applicationParams),
          2 * 60 * 1000,
          { forceRefresh }
        )
        : Promise.resolve({ data: { results: [] } });

      const [results, applicationRes] = await Promise.all([
        Promise.all(fetchPromises),
        applicationPromise,
      ]);
      const [enquiryRes, offerRes, inspectionRes, renterRes, processedRes] = results;

      const enquiries = (enquiryRes.data?.results || []).map(l => ({ ...l, lead_type: l.lead_type || 'buyer_enquiry' }));
      const offers = (offerRes.data?.results || []).map(l => ({ ...l, lead_type: l.lead_type || 'offer' }));
      const inspections = (inspectionRes.data?.results || []).map(l => ({ ...l, lead_type: l.lead_type || 'inspection' }));
      const renters = (renterRes.data?.results || []).map(l => ({ ...l, lead_type: l.lead_type || 'renter_enquiry' }));
      const applications = (applicationRes?.data?.results || []).map(l => ({ ...l, lead_type: 'agent_application' }));
      const processed = data.includeProcessed ? (processedRes?.data?.results || []).map(l => ({ ...l, lead_type: l.lead_type || 'on_market', is_processed: true })) : [];

      setAnalytics(enquiryRes.data?.analytics || {});
      setPageCount(enquiryRes.data?.total_pages || 1);
      setNextPageUrl(enquiryRes.data?.next);
      setPrevPageUrl(enquiryRes.data?.previous);
      setAnalytics(enquiryRes.data?.analytics || {});

      setLeadsList([...enquiries, ...offers, ...inspections, ...renters, ...applications, ...processed]);



      setPageCount(enquiryRes.data?.total_pages || 1);
      setNextPageUrl(enquiryRes.data?.next);
      setPrevPageUrl(enquiryRes.data?.previous);
      setAnalytics(enquiryRes.data?.analytics || {});
    } catch (err) {
      setLeadsList([]);
      setError("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  // Rows are grouped (a buyer can have >1 underlying Lead row for the same
  // contact+property, e.g. an enquiry lead plus a later offer lead). The
  // checkbox must select/deselect every id in the group, not just the first
  // one, or "Ignore Selected" only marks part of the group and the row
  // reappears once its surviving sibling re-groups on the next fetch.
  const toggleGroupSelection = (groupIds) => {
    setSelectedLeads((prev) => {
      const allSelected = groupIds.every((id) => prev.includes(id));
      return allSelected
        ? prev.filter((id) => !groupIds.includes(id))
        : [...new Set([...prev, ...groupIds])];
    });
  };

  const handleConfirmDelete = async () => {

    if (!leadToDelete) return;

    setDeletingId(leadToDelete.id);
    try {
      await deleteLead(leadToDelete.id);

      // Invalidate cache to ensure fresh data
      invalidateCache.leads();

      toast.success("Lead deleted");
      setShowDeleteModal(false);
      setLeadToDelete(null);
      fetchLeads();
    } catch {
      toast.error("Failed to delete lead");
    } finally {
      setDeletingId(null);
    }
  };

  // The Ignored tab. Fetched separately from the holding area so the badge
  // counts on the other tabs stay exactly what they were.
  const fetchIgnoredLeads = async () => {
    setIgnoredLoading(true);
    try {
      const types = ['buyer_enquiry', 'offer', 'inspection', 'renter_enquiry'];
      if (isAdmin) types.push('agent_application');
      const responses = await Promise.all(
        types.map((type) => getLeads({ type, ignored: 'true', page_size: 100, ...scopeParams }))
      );
      const rows = responses.flatMap((res, i) =>
        (res.data?.results || []).map((l) => ({ ...l, lead_type: l.lead_type || types[i] }))
      );
      setIgnoredList(rows);
    } catch {
      setIgnoredList([]);
    } finally {
      setIgnoredLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'ignored') {
      fetchIgnoredLeads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, scopeKey]);

  useEffect(() => {
    // Fetch all leads on mount and when page or filters change
    fetchLeads({
      search: searchTerm,
      source: selectedSource,
      priority: selectedPriority,
      stage: selectedStatus,
      settlement_date: selectedSettlementDate,
      includeProcessed: showProcessed,
    }, currentPage);
    // isAdmin is in the deps because `user` arrives from the auth store after
    // the first render: without it the Agent Applications tab would stay empty
    // until something else triggered a refetch.
  }, [currentPage, searchTerm, selectedSource, selectedPriority, selectedStatus, selectedSettlementDate, showProcessed, isAdmin, scopeKey]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setFilterDropdownOpen(false);
      }
      if (sourceRef.current && !sourceRef.current.contains(event.target)) {
        setSourceDropdownOpen(false);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setStatusDropdownOpen(false);
      }
      if (
        settlementDateRef.current &&
        !settlementDateRef.current.contains(event.target)
      ) {
        setSettlementDateDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch voice agents for modal
  useEffect(() => {
    if (isCallModalOpen) {
      api
        .get("api/ai-voice-caller/voice-agents/")
        .then((res) => {
          // Handle both paginated response {results: []} and direct array response
          const agentsData = Array.isArray(res.data)
            ? res.data
            : (res.data?.results || []);
          setVoiceAgents(agentsData);
        })
        .catch(() => setVoiceAgents([]));
    }
  }, [isCallModalOpen]);

  // Fetch documents and transcriptions when lead detail modal opens
  useEffect(() => {
    if (isDetailModalOpen && selectedLead?.id) {
      fetchLeadDocuments(selectedLead.id);

      // Also fetch call transcriptions for this lead
      const fetchTranscription = async () => {
        try {
          const response = await api.get("api/ai-voice-caller/agent-calls/", {
            params: { lead_id: selectedLead.id }
          });
          const leadCalls = response.data || [];

          if (leadCalls.length > 0) {
            // Sort by latest just in case
            const sortedCalls = leadCalls.sort((a, b) => new Date(b.created_at || b.start_datetime) - new Date(a.created_at || a.start_datetime));

            // Find the latest call that actually has a transcription
            const callWithTranscript = sortedCalls.find(call =>
              Array.isArray(call.conversation_history) && call.conversation_history.length > 0
            );

            const lastTranscription = callWithTranscript ? callWithTranscript.conversation_history : [];
            setSelectedLead(prev => ({ ...prev, transcription: lastTranscription }));
          }
        } catch (error) {
          console.error("Failed to fetch lead transcription:", error);
        }
      };

      fetchTranscription();
    }
  }, [isDetailModalOpen, selectedLead?.id]);

  const priorities = ["High", "Medium", "Low"];

  const settlementDateOptions = [
    { value: "today", label: "Today" },
    { value: "next_30_days", label: "Next 30 Days" },
    { value: "next_60_days", label: "Next 60 Days" },
    { value: "next_90_days", label: "Next 90 Days" },
    { value: "last_30_days", label: "Last 30 Days" },
    { value: "last_60_days", label: "Last 60 Days" },
    { value: "last_90_days", label: "Last 90 Days" },
  ];

  // const tabs = [
  //   {
  //     key: "new",
  //     label: "New Leads",
  //     count: leadsList.filter((d) =>
  //       ["New Lead", "Attempting Contact"].includes(d.stage)
  //     ).length,
  //     icon: <Users size={16} />,
  //   },
  //   {
  //     key: "progress",
  //     label: "In Progress",
  //     count: leadsList.filter((d) =>
  //       [
  //         "Contact - Not Qualified",
  //         "Contacted Nurture",
  //         "Booked Appraisal",
  //         "Appraisal Complete - Awaiting Decision",
  //         "Follow-Up Ongoing",
  //       ].includes(d.stage)
  //     ).length,
  //     icon: <Filter size={16} />,
  //   },
  //   {
  //     key: "closed",
  //     label: "Closed",
  //     count: leadsList.filter((d) =>
  //       [
  //         "Won - Listing Signed",
  //         "Lost - Listed With Another Agent",
  //         "Unresponsive - Cold",
  //       ].includes(d.stage)
  //     ).length,
  //     icon: <Users size={16} />,
  //   },
  // ];

  // Brand Colors
  const brandColors = {
    primary: "#575955",
    secondary: "#55C5D0",
  };

  // Lead Sources for Australian Real Estate
  const leadSources = [
    { id: "rea", name: "realestate.com.au", icon: "🏠" },
    { id: "form", name: "Form", icon: "📝" },
    { id: "buyer_portal", name: "Buyer Portal", icon: "🛒" },
    { id: "chatbot", name: "Chatbot", icon: "🤖" },
    { id: "website", name: "Website", icon: "🌐" },
    { id: "referral", name: "Referral", icon: "👥" },
    // { id: "domain", name: "Domain.com.au", icon: "🏢" },
    // { id: "allhomes", name: "Allhomes.com.au", icon: "🏘️" },
    // { id: "onthehouse", name: "OnTheHouse.com.au", icon: "🏡" },
    // { id: "rent", name: "Rent.com.au", icon: "🔑" },
    // { id: "realcommercial", name: "RealCommercial.com.au", icon: "🏪" },
    { id: "direct", name: "Direct Contact", icon: "📞" },
    { id: "voiceCaller", name: "Voice Caller", icon: "🗣️" },
    { id: "chatbot", name: "ChatBot", icon: "🤖" },
    { id: "referral", name: "Referral", icon: "👥" },
    { id: "social", name: "Social Media", icon: "📱" },
    { id: "other", name: "Other Sources", icon: "📌" },
  ];

  const getSourceDetails = (sourceId) => {
    return (
      leadSources.find((source) => source.id === sourceId) || {
        name: "Unknown",
        icon: "❓",
      }
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "hot":
        return "bg-red-100 text-red-700";
      case "warm":
        return "bg-orange-100 text-orange-700";
      case "cold":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  // Add new lead handler (for now, just logs the data)
  const handleAddLead = async (leadData) => {
    try {
      setLoading(true);

      // Extract first owner's data from owners array
      const firstOwner = leadData.owners?.[0] || {};

      // Source mapping to match backend expectations
      const sourceMapping = {
        'Direct': 'direct',
        'Referral': 'referral',
        'Portal': 'website',
      };

      const payload = {
        name: leadData.full_name || leadData.name || leadData.owners?.[0]?.full_name || '',
        email: leadData.email || leadData.owners?.[0]?.email || '',
        phone: leadData.contact || leadData.phone || (leadData.owners?.[0]?.contact ? `${leadData.owners?.[0]?.country?.dial || ''}${leadData.owners?.[0]?.contact}` : ''),
        property_address: leadData.property_address || '',
        source: sourceMapping[leadData.source] || leadData.source?.toLowerCase() || 'direct',
        stage: leadData.stage || leadData.status || 'New Lead',
        priority: leadData.priority || 'medium',
        timeline: leadData.timeline || '',
        initial_notes: leadData.initial_notes || leadData.notes || '',
        agent: leadData.agent || null,
        buyer_budget_min: leadData.min_price ? parseInt(leadData.min_price) : null,
        buyer_budget_max: leadData.max_price ? parseInt(leadData.max_price) : null,
      };
      if (leadData.price_range && (!payload.buyer_budget_min && !payload.buyer_budget_max)) {
        const parts = leadData.price_range.split('-');
        if (parts[0]) payload.buyer_budget_min = parseInt(parts[0].replace(/[^0-9]/g, ''));
        if (parts[1]) payload.buyer_budget_max = parseInt(parts[1].replace(/[^0-9]/g, ''));
      }

      await addLead(payload);

      // Invalidate cache to ensure fresh data
      invalidateCache.leads();

      toast.success("Lead added successfully!");
      fetchLeads();
      setIsAddModalOpen(false);
    } catch (error) {
      console.error("Error adding lead:", error);
      const errorMessage = error?.response?.data?.detail ||
        error?.response?.data?.message ||
        JSON.stringify(error?.response?.data) ||
        "Failed to add lead";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Add these helper functions for color coding
  const getStageColor = (stage) => {
    switch (stage) {
      case "New Lead":
        return "bg-blue-100 text-blue-700 border border-blue-200";
      case "Attempting Contact":
        return "bg-cyan-100 text-cyan-700 border border-cyan-200";
      case "Contact - Not Qualified":
        return "bg-gray-200 text-gray-700 border border-gray-300";
      case "Contacted Nurture":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      case "Booked Appraisal":
        return "bg-purple-100 text-purple-700 border border-purple-200";
      case "Appraisal Complete - Awaiting Decision":
        return "bg-pink-100 text-pink-700 border border-pink-200";
      case "Follow-Up Ongoing":
        return "bg-indigo-100 text-indigo-700 border border-indigo-200";
      case "Won - Listing Signed":
        return "bg-green-100 text-green-700 border border-green-200";
      case "Lost - Listed With Another Agent":
        return "bg-red-200 text-red-800 border border-red-300";
      case "Unresponsive - Cold":
        return "bg-gray-300 text-gray-800 border border-gray-400";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-200";
    }
  };
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "bg-red-100 text-red-700";
      case "Medium":
        return "bg-yellow-100 text-yellow-800";
      case "Low":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const openCallModal = (lead) => {
    // console.log(lead);
    setSelectedLead(lead);
    setIsCallModalOpen(true);
  };
  const closeCallModal = () => {
    setIsCallModalOpen(false);
    setSelectedLead(null);
    setSelectedVoiceAgent("");
  };
  const handleCall = async () => {
    if (!selectedLead || !selectedVoiceAgent) return;
    setIsCalling(true);
    try {
      await api.post("/ai-voice-caller/ai-voice-call/", {
        lead: selectedLead.id,
        voice_agent: selectedVoiceAgent,
      });
      toast.success("Call initiated successfully");
      closeCallModal();
    } catch (e) {
      toast.error("Failed to initiate call");
    } finally {
      setIsCalling(false);
    }
  };

  // Document management functions
  const fetchLeadDocuments = async (leadId) => {
    if (!leadId) return;

    try {
      setLoadingLeadDocs(true);
      const response = await documentsAPI.getDocuments({ related_lead: leadId });
      setLeadDocuments(Array.isArray(response) ? response : response.results || []);
    } catch (error) {
      console.error('Error fetching lead documents:', error);
      setLeadDocuments([]);
    } finally {
      setLoadingLeadDocs(false);
    }
  };

  const handleLeadFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedLeadFile(file);
    }
  };

  const handleUploadLeadDocument = async () => {
    if (!selectedLeadFile) {
      toast.error('Please select a file');
      return;
    }

    if (!selectedLead?.id) {
      toast.error('No lead selected');
      return;
    }

    setUploadingLeadDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedLeadFile);
      formData.append('name', selectedLeadFile.name);
      formData.append('description', `Document for lead: ${selectedLead.full_name}`);
      formData.append('category', 'miscellaneous');
      formData.append('related_lead', selectedLead.id);

      await documentsAPI.uploadDocument(formData);
      toast.success('Document uploaded successfully!');
      setSelectedLeadFile(null);
      fetchLeadDocuments(selectedLead.id);
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploadingLeadDoc(false);
    }
  };

  const handleDownloadLeadDocument = async (doc) => {
    try {
      const blob = await documentsAPI.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Document downloaded');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDeleteLeadDocument = (doc) => {
    setDocToDelete(doc);
    setShowDocDeleteModal(true);
  };

  const handleConfirmDocDelete = async () => {
    if (!docToDelete) return;

    setDeletingDoc(true);
    try {
      await documentsAPI.deleteDocument(docToDelete.id);
      toast.success('Document deleted successfully!');
      if (selectedLead?.id) {
        fetchLeadDocuments(selectedLead.id);
      }
      setShowDocDeleteModal(false);
      setDocToDelete(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    } finally {
      setDeletingDoc(false);
    }
  };

  const DashboardCard = ({ icon, title, value }) => (
    <div className="bg-white p-4 rounded-lg shadow flex items-center  w-80">
      <div className="bg-[#55c5d0] p-2 rounded-md mr-5">
        <span className="text-white">{icon}</span>
      </div>
      <div>
        <p className="text-gray-600 text-sm">{title}</p>
        <p className="text-2xl font-bold">{value}</p>
      </div>
    </div>
  );

  useEffect(() => {
    setCurrentPage(1);
    setUiPage(1);
  }, [searchTerm, selectedSource, selectedPriority, selectedStatus, selectedSettlementDate, showProcessed]);

  // Reset to the first page whenever the tab, page size, or in-tab filters
  // change, so the user never lands on an out-of-range page.
  useEffect(() => {
    setUiPage(1);
  }, [activeTab, pageSize, selectedTypeFilter, selectedPropertyFilter, selectedAgentFilter, selectedDateFilter]);

  // No longer need viewMode specific fetch as the main useEffect handles it
  // and we're always using page_size: 100 for this holding area.

  // Map analytics keys to icons
  const analyticsIcons = {
    "New Lead": <UserPlus />,
    "Attempting Contact": <PhoneCall />,
    "Contact - Not Qualified": <UserX />,
    "Contacted Nurture": <UserCheck />,
    "Booked Appraisal": <Award />,
    "Appraisal Complete - Awaiting Decision": <UserCog />,
    "Follow-Up Ongoing": <CircleIcon />,
    "Won - Listing Signed": <Star />,
    "Lost - Listed With Another Agent": <UserMinus />,
    "Unresponsive - Cold": <User />,
    initial_contact: <Phone />,
  };

  // Skeleton component for analytics cards
  const DashboardCardSkeleton = () => (
    <div className="bg-white p-4 rounded-lg shadow flex items-center w-80 animate-pulse">
      <div className="bg-gray-200 p-2 rounded-md mr-5 w-10 h-10" />
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
        <div className="h-6 bg-gray-200 rounded w-16" />
      </div>
    </div>
  );

  // Group enquiries by contact (email or phone) and show count
  const groupedLeads = React.useMemo(() => {
    const groups = {};
    leadsList.forEach((lead) => {
      const key = (lead.email || '').toLowerCase().trim() || (lead.contact || lead.phone || '').trim();
      if (!key) {
        groups[lead.id] = [lead];
        return;
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(lead);
    });
    // Return array of { representative, count, leads }
    return Object.values(groups).map((group) => ({
      representative: group[0],
      count: group.length,
      leads: group,
    }));
  }, [leadsList]);

  // quick=true opens a lightweight property picker (Add to Property): pick a
  // property, attach, and the enquiry drops out of the holding area — no full
  // lead-preview modal. quick=false (View) opens the full LeadPreviewModal.
  // CG (#12973323252): "just send it straight to the property, no need to bring
  // up the property profile page. That should only happen if we click view."
  const openAssignModal = async (groupOrLead, quick = false) => {
    const lead = Array.isArray(groupOrLead) ? groupOrLead[0] : groupOrLead;
    const group = Array.isArray(groupOrLead) ? groupOrLead : [groupOrLead];

    setAssignPropertyModal({ open: true, lead, group, quick });
    setAssignPropertyId('');
    setAssignMessage(lead.initial_notes || '');
    setAssignPropertiesLoading(true);
    try {
      // Fallback picker (only reached when an enquiry has no source property).
      // Scope it to the caller's own listings for a plain agent — Paul, CRM-2026
      // -00052: "coming up with all properties in the whole office from all
      // agents." Admins/PAs still get the agency-wide list.
      const res = await getProperties({ page_size: 100, agency_wide: isAdmin });
      const list = res?.data?.results || res?.data || [];
      setAssignProperties(Array.isArray(list) ? list : []);
    } catch {
      setAssignProperties([]);
    } finally {
      setAssignPropertiesLoading(false);
    }
  };

  const handleAssignToProperty = async () => {
    const lead = assignPropertyModal.lead;
    const group = assignPropertyModal.group || [lead];

    if (!lead || !assignPropertyId) {
      toast.error('Please select a property.');
      return;
    }
    setAssigningLeadId(lead.id);
    try {
      // Process all leads in the group
      await Promise.all(group.map(l =>
        addLeadToPropertyEnquiry(l.id, parseInt(assignPropertyId), assignMessage)
      ));

      invalidateCache.leads();
      toast.success(group.length > 1 ? 'Enquiries assigned to property!' : 'Enquiry assigned to property!');

      // Remove only the specific leads that were assigned (by ID), not all with same email/phone
      const assignedIds = new Set(group.map(gl => gl.id));
      setLeadsList((prev) => prev.filter((l) => !assignedIds.has(l.id)));
      setAssignPropertyModal({ open: false, lead: null });
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.detail || 'Failed to assign.');
    } finally {
      setAssigningLeadId(null);
    }
  };

  // "Add to Property" — a buyer enquiry comes from ONE property, so attach it
  // straight to that property instead of asking the agent to pick from a list of
  // every office listing (Paul, CRM-2026-00052). The source property is on the
  // enquiry itself (Lead.property). Only when it is missing do we fall back to
  // the (agent-scoped) picker.
  const propIdOf = (l) => (l && typeof l.property === 'object' ? l.property?.id : l?.property) || null;
  const handleAddToProperty = async (groupOrLead) => {
    const group = Array.isArray(groupOrLead) ? groupOrLead : [groupOrLead];
    // Every enquiry in the group must carry its own source property; otherwise
    // fall back to the picker so nothing is attached to the wrong listing.
    if (!group.length || group.some((l) => !propIdOf(l))) {
      openAssignModal(group, true);
      return;
    }
    setAssigningLeadId(group[0].id);
    try {
      await Promise.all(group.map((l) =>
        addLeadToPropertyEnquiry(l.id, propIdOf(l), l.initial_notes || '')
      ));
      invalidateCache.leads();
      const assignedIds = new Set(group.map((g) => g.id));
      setLeadsList((prev) => prev.filter((l) => !assignedIds.has(l.id)));
      const propId = propIdOf(group[0]);
      const sameProp = group.every((l) => propIdOf(l) === propId);
      toast.success((t) => (
        <span className="flex items-center gap-2">
          {group.length > 1 ? 'Enquiries added to their property.' : 'Enquiry added to the property.'}
          {sameProp && (
            <button
              type="button"
              className="font-semibold text-[#128f89] underline"
              onClick={() => { toast.dismiss(t.id); navigate(`/properties/${propId}`); }}
            >
              View property
            </button>
          )}
        </span>
      ));
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.detail || 'Failed to add to property.');
    } finally {
      setAssigningLeadId(null);
    }
  };

  // Ignore takes the row out of the holding area and nothing more. It used to
  // delete the lead, which took the contact and their enquiry history out of the
  // CRM with it: "We dont want the contacts to delete. We just want the contact
  // to leave our buyer enquiries tab" (Christian Gravias, Monday 12838026421).
  // Everything ignored is still there under the Ignored tab, and can be restored.
  const handleIgnoreLead = async (groupOrLead, reason, notes) => {
    const leadsToIgnore = Array.isArray(groupOrLead) ? groupOrLead : [groupOrLead];
    const representativeLead = leadsToIgnore[0];

    setIgnoringLeadId(representativeLead.id);
    try {
      await Promise.all(leadsToIgnore.map(l => ignoreLead(l.id, { reason, notes })));

      invalidateCache.leads();

      const ignoredIds = new Set(leadsToIgnore.map(l => l.id));
      setLeadsList((prev) => prev.filter((l) => !ignoredIds.has(l.id)));

      toast.success(leadsToIgnore.length > 1 ? 'Enquiries moved to Ignored.' : 'Enquiry moved to Ignored.');
    } catch {
      toast.error('Failed to ignore enquiry.');
    } finally {
      setIgnoringLeadId(null);
    }
  };

  // AddToDatabaseModal only files the buyer into a Prospecting database
  // category (createProspect) - it has no idea this lead came from the Buyer
  // Enquiries holding area, so it never touches the Lead row itself. Without
  // this, the enquiry never left the tab: nothing marked it actioned, so a
  // refresh (or even the next render) brought it right back. Mirrors
  // handleIgnoreLead / SellerLeadsPage.handleDatabaseSaved - ignoring is
  // exactly "this enquiry has been handled" for this page.
  // An offer declined straight from its row (via OfferWorkflowActions): the
  // linked property Offer is already marked Declined, so drop the lead from the
  // Offers holding area and mark it handled - same as declining from the View
  // pop-up (see the LeadPreviewModal onDecline wiring below).
  const handleOfferRowDeclined = (lead) => {
    if (!lead) return;
    setLeadsList((prev) => prev.filter((l) => l.id !== lead.id));
    ignoreLead(lead.id, { reason: 'offer_declined' })
      .then(() => invalidateCache.leads())
      .catch(() => { });
  };

  const handleDatabaseSaved = async (lead) => {
    if (!lead) return;
    try {
      // Named, so Agent Performance reads "Added to Database", not "Not specified".
      await ignoreLead(lead.id, { reason: 'added_to_database' });
      invalidateCache.leads();
      setLeadsList((prev) => prev.filter((l) => l.id !== lead.id));
    } catch (err) {
      // The Database record is already saved, so never surface this as a
      // failure - the row just stays on the list until manually ignored.
      console.error('Failed to mark the buyer enquiry as actioned:', err);
    }
  };

  // Restore, from the Ignored tab. Puts the row back in the holding area.
  const handleRestoreLead = async (groupOrLead) => {
    const leadsToRestore = Array.isArray(groupOrLead) ? groupOrLead : [groupOrLead];
    const representativeLead = leadsToRestore[0];

    setRestoringLeadId(representativeLead.id);
    try {
      await Promise.all(leadsToRestore.map(l => ignoreLead(l.id, { ignored: false })));

      invalidateCache.leads();

      const restoredIds = new Set(leadsToRestore.map(l => l.id));
      setIgnoredList((prev) => prev.filter((l) => !restoredIds.has(l.id)));

      toast.success(leadsToRestore.length > 1 ? 'Enquiries restored.' : 'Enquiry restored.');
    } catch {
      toast.error('Failed to restore enquiry.');
    } finally {
      setRestoringLeadId(null);
    }
  };

  // Ignore Selected opens the same "why ignore?" modal as the row button, so a
  // bulk ignore can't leave every lead reading "Not specified" (Michael Piccoli,
  // Monday 13225628427). Restore Selected only needs a yes/no.
  const handleBulkIgnore = () => {
    if (selectedLeads.length === 0) return;
    if (activeTab !== 'ignored') {
      setBulkIgnoreOpen(true);
      return;
    }
    const noun = selectedLeads.length === 1 ? 'item' : 'items';
    if (!window.confirm(`Restore ${selectedLeads.length} selected ${noun} to the holding area?`)) return;
    runBulkIgnore(true);
  };

  const runBulkIgnore = async (restoring, reason, notes) => {
    setBulkIgnoring(true);
    try {
      await bulkIgnoreLeads(selectedLeads, { ignored: !restoring, reason, notes });
      invalidateCache.leads();

      const clearedIds = new Set(selectedLeads);
      if (restoring) {
        setIgnoredList((prev) => prev.filter((l) => !clearedIds.has(l.id)));
      } else {
        setLeadsList((prev) => prev.filter((l) => !clearedIds.has(l.id)));
      }
      setSelectedLeads([]);
      const label = clearedIds.size === 1 ? 'enquiry' : 'enquiries';
      toast.success(restoring
        ? `${clearedIds.size} ${label} restored.`
        : `${clearedIds.size} ${label} moved to Ignored.`);
    } catch {
      toast.error(restoring
        ? 'Failed to restore the selected enquiries.'
        : 'Failed to ignore the selected enquiries.');
    } finally {
      setBulkIgnoring(false);
    }
  };

  const toggleMessageExpand = (id) => {
    setExpandedMessages((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const sourceLabel = (source) => {
    if (!source) return 'Unknown';
    const map = {
      buyer_portal: 'Buyer Portal',
      realestate: 'realestate.com.au',
      website: 'Website',
      chatbot: 'Chatbot',
      direct: 'Direct',
      form: 'Form',
      referral: 'Referral',
      voiceCaller: 'Voice Caller',
      rea: 'REA',
      domain: 'Domain',
    };
    return map[source.toLowerCase()] || source;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
    return ((parts[0][0] || '') + (parts[parts.length - 1][0] || '')).toUpperCase();
  };

  const formatMoney = (val) => {
    if (val === undefined || val === null || val === '') return null;

    // If it's a string, handle ranges and strip non-numeric chars
    let cleanVal = String(val);

    // If it contains a range (e.g. "1M - 2M"), take the first part
    if (cleanVal.includes('-')) {
      cleanVal = cleanVal.split('-')[0].trim();
    }

    // Remove currency symbols, commas, and other non-numeric chars except decimal point
    cleanVal = cleanVal.replace(/[^\d.]/g, '');

    const num = parseFloat(cleanVal);
    if (isNaN(num)) return null;

    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: 0
    }).format(num);
  };

  return (
    <div className="w-full">
      <PageHeader
        title={activeTab === 'applications' ? 'Agent Applications' : 'Buyer Enquiries & Offers'}
        subtitle={activeTab === 'applications'
          ? 'Agent applications from the Facebook recruitment ads. Visible to admins only.'
          : 'Review and qualify buyer enquiries and offers. Assign them to properties, or ignore to move them to the Ignored tab. Ignoring never deletes a contact.'}
        action={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {activeTab !== 'applications' && (canScope || canIncludeAgents) && (
              <>
                {canScope && (
                  <AgencyScopeSwitch
                    agencyWide={agencyWide}
                    onChange={setAgencyWide}
                    mineLabel="My enquiries"
                    agencyLabel="All agents"
                  />
                )}
                {!agencyWide && canIncludeAgents && (
                  <IncludeAgentsPicker
                    agents={agents}
                    selectedIds={extraAgentIds}
                    onToggle={toggleAgentId}
                    onClear={() => setExtraAgentIds([])}
                    currentUserId={user?.id}
                  />
                )}
              </>
            )}
            <button
              onClick={() => fetchLeads({
                search: searchTerm,
                source: selectedSource,
                priority: selectedPriority,
                stage: selectedStatus,
                settlement_date: selectedSettlementDate,
                includeProcessed: showProcessed,
              }, currentPage, true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#55C5D0] text-white rounded-xl hover:bg-[#3aa8b4] transition-all font-semibold shadow-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Refreshing...' : 'Refresh Enquiries'}
            </button>
          </div>
        }
      />

      {/* Say plainly whose rows are on screen. The old page called everything
          "yours" while showing the whole office, which is exactly how four
          other agents' enquiries went unnoticed in an admin's list. */}
      {canScope && agencyWide && activeTab !== 'applications' && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
          <Users className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Showing <strong>every agent's</strong> enquiries, offers and inspection requests,
            not just your own. Switch to "My enquiries" above to see only yours.
          </span>
        </div>
      )}

      {/* Vault-style Holding Area */}
      <div className="bg-white shadow-sm rounded-2xl p-3 sm:p-4 md:p-6">
        {/* Always-visible horizontal scrollbar for the enquiries table, so
            agents know they can scroll sideways to reach every column. */}
        <style>{`
          .enquiry-hscroll { scrollbar-width: thin; scrollbar-color: #94a3b8 #e2e8f0; }
          .enquiry-hscroll::-webkit-scrollbar { height: 10px; }
          .enquiry-hscroll::-webkit-scrollbar-track { background: #e2e8f0; border-radius: 8px; }
          .enquiry-hscroll::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 8px; }
          .enquiry-hscroll::-webkit-scrollbar-thumb:hover { background: #64748b; }
        `}</style>

        {/* Tabs: Enquiries | Offers */}
        <div className="flex flex-wrap items-center gap-1 mb-6 border-b border-gray-100">
          <button
            onClick={() => navigate('/leads?tab=enquiries')}
            className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition-all ${activeTab === 'enquiries'
              ? 'text-[#55C5D0] border-b-2 border-[#55C5D0] -mb-px bg-[#f0fafb]'
              : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            <span className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Buyer Enquiries
              {enquiryCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white shadow-sm">
                  {enquiryCount > 99 ? '99+' : enquiryCount}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => navigate('/leads?tab=offers')}
            className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition-all ${activeTab === 'offers'
              ? 'text-[#55C5D0] border-b-2 border-[#55C5D0] -mb-px bg-[#f0fafb]'
              : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            <span className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              Offers
              {offerCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white shadow-sm">
                  {offerCount > 99 ? '99+' : offerCount}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => navigate('/leads?tab=inspections')}
            className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition-all ${activeTab === 'inspections'
              ? 'text-[#55C5D0] border-b-2 border-[#55C5D0] -mb-px bg-[#f0fafb]'
              : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            <span className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              Inspection Requests
              {inspectionCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white shadow-sm">
                  {inspectionCount > 99 ? '99+' : inspectionCount}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => navigate('/leads?tab=renters')}
            className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition-all ${activeTab === 'renters'
              ? 'text-[#55C5D0] border-b-2 border-[#55C5D0] -mb-px bg-[#f0fafb]'
              : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            <span className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Renter Enquiries
              {renterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white shadow-sm">
                  {renterCount > 99 ? '99+' : renterCount}
                </span>
              )}
            </span>
          </button>
          {/* Agent Applications - people answering the Facebook recruitment
              ads. Admin-only: "please create an agent recruitment tab that only
              I get" (Christian Gravias, Monday 12889580221). Hidden here and
              refused by the API, so it is not a matter of knowing the URL. */}
          {isAdmin && (
            <button
              onClick={() => navigate('/leads?tab=applications')}
              className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition-all ${activeTab === 'applications'
                ? 'text-[#55C5D0] border-b-2 border-[#55C5D0] -mb-px bg-[#f0fafb]'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <span className="flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Agent Applications
                {applicationCount > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-purple-500 text-white shadow-sm">
                    {applicationCount > 99 ? '99+' : applicationCount}
                  </span>
                )}
              </span>
            </button>
          )}
          {/* Ignored. Nothing here has been deleted, every row can be restored. */}
          <button
            onClick={() => navigate('/leads?tab=ignored')}
            className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition-all ${activeTab === 'ignored'
              ? 'text-[#55C5D0] border-b-2 border-[#55C5D0] -mb-px bg-[#f0fafb]'
              : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            <span className="flex items-center gap-2">
              <Archive className="w-4 h-4" />
              Ignored
              {ignoredList.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-gray-200 text-gray-600">
                  {ignoredList.length > 99 ? '99+' : ignoredList.length}
                </span>
              )}
            </span>
          </button>

          {/* Show Processed Toggle */}
          <div className="ml-4 flex items-center gap-2">
            <button
              onClick={() => setShowProcessed(!showProcessed)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                showProcessed 
                  ? 'bg-[#55C5D0] text-white shadow-sm' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <CheckCircle className="w-3.5 h-3.5" />
              {showProcessed ? 'Showing All' : 'Show Processed'}
            </button>
          </div>

          {/* Search on the right */}
          <div className="ml-auto relative w-full sm:w-auto mt-2 sm:mt-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, phone..."
              className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25 w-full sm:w-80 max-w-full"
            />
          </div>
        </div>

        {/* Filters row */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <select
            value={selectedTypeFilter}
            onChange={(e) => setSelectedTypeFilter(e.target.value)}
            className="text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25 bg-white"
          >
            <option value="all">All Types</option>
            {typeFilterOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          <select
            value={selectedDateFilter}
            onChange={(e) => setSelectedDateFilter(e.target.value)}
            className="text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25 bg-white"
          >
            {DATE_RANGE_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>

          <select
            value={selectedPropertyFilter}
            onChange={(e) => setSelectedPropertyFilter(e.target.value)}
            className="text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25 bg-white max-w-[220px]"
          >
            <option value="all">All Properties</option>
            {propertyFilterOptions.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>

          <select
            value={selectedAgentFilter}
            onChange={(e) => setSelectedAgentFilter(e.target.value)}
            className="text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25 bg-white"
          >
            <option value="all">All Agents</option>
            {agentFilterOptions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>

          <select
            value={selectedSource || 'all'}
            onChange={(e) => setSelectedSource(e.target.value === 'all' ? null : e.target.value)}
            className="text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25 bg-white"
          >
            <option value="all">All Sources</option>
            {leadSources.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          {(selectedTypeFilter !== 'all' || selectedDateFilter !== 'all' || selectedPropertyFilter !== 'all' || selectedAgentFilter !== 'all' || selectedSource) && (
            <button
              onClick={() => {
                setSelectedTypeFilter('all');
                setSelectedDateFilter('all');
                setSelectedPropertyFilter('all');
                setSelectedAgentFilter('all');
                setSelectedSource(null);
              }}
              className="text-xs font-semibold text-gray-400 hover:text-gray-600 px-2 py-1.5"
            >
              Clear filters
            </button>
          )}

          {selectedLeads.length > 0 && activeTab !== 'offers' && (
            <button
              onClick={handleBulkIgnore}
              disabled={bulkIgnoring}
              className={`ml-auto flex items-center gap-1.5 text-xs font-semibold disabled:opacity-50 border rounded-lg px-3 py-1.5 transition-all ${activeTab === 'ignored'
                ? 'text-[#55C5D0] border-[#55C5D0]/40 hover:bg-[#f0fafb]'
                : 'text-orange-600 hover:text-orange-700 border-orange-200 hover:bg-orange-50'
                }`}
            >
              <X className="w-3.5 h-3.5" />
              {bulkIgnoring
                ? (activeTab === 'ignored' ? 'Restoring...' : 'Ignoring...')
                : activeTab === 'ignored'
                  ? `Restore Selected (${selectedLeads.length})`
                  : `Ignore Selected (${selectedLeads.length})`}
            </button>
          )}
        </div>

        {(activeTab === 'ignored' ? ignoredLoading : loading) ? (
          <div className="text-center py-16 text-gray-400 flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-[#55C5D0]/30 border-t-[#55C5D0] rounded-full animate-spin" />
            <span>Loading...</span>
          </div>
        ) : error ? (
          <div className="text-center py-16 text-red-500">{error}</div>
        ) : (() => {
          const { groupEntries, filteredLeads } = groupedLeadsData;

          // Client-side pagination of the displayed rows (all tabs).
          const uiPageCount = Math.max(1, Math.ceil(groupEntries.length / pageSize));
          const safePage = Math.min(uiPage, uiPageCount);
          const pagedEntries = groupEntries.slice((safePage - 1) * pageSize, safePage * pageSize);

          if (groupEntries.length === 0) {
            return (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-20 h-20 rounded-2xl bg-[#f0fafb] flex items-center justify-center mb-4">
                  <Inbox className="w-10 h-10 text-[#55C5D0]" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-1">All clear!</h3>
                <p className="text-gray-400 text-sm max-w-xs text-center">
                  {activeTab === 'ignored'
                    ? 'Nothing ignored. Anything you ignore lands here and can be restored.'
                    : activeTab === 'enquiries'
                      ? 'No pending buyer enquiries. New enquiries will appear here.'
                      : activeTab === 'offers'
                        ? 'No pending offers. Offers submitted via the Buyer Portal will appear here.'
                        : activeTab === 'renters'
                          ? 'No pending renter enquiries. Enquiries on rental listings will appear here.'
                          : activeTab === 'applications'
                            ? 'No agent applications yet. Enquiries from the Facebook recruitment ads land here.'
                            : 'No pending inspection requests. Buyer Portal inspection registrations will appear here.'}
                </p>
              </div>
            );
          }

          return (
            <>
            <div className="enquiry-hscroll overflow-x-auto pb-2">
              <table className="w-full min-w-[800px] text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    {/* No bulk-select on the Offers tab: an offer is declined
                        one at a time with vendor context, not ignored in bulk
                        (Christian Gravias, Monday 13099979503). */}
                    {activeTab !== 'offers' && (
                      <th className="w-10 px-3 py-3">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0]"
                          checked={filteredLeads.length > 0 && selectedLeads.length === filteredLeads.length}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedLeads(filteredLeads.map(l => l.id));
                            else setSelectedLeads([]);
                          }}
                        />
                      </th>
                    )}
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3 w-36">Received</th>
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3 w-28">Type</th>
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3">Name</th>
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3">Property</th>
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3">Agents</th>
                    <th className="text-center text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3">Price</th>
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3">Source</th>
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3">Status</th>
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3 min-w-[240px] w-[260px]">Comments</th>
                    <th className="md:sticky md:right-0 md:z-10 bg-white border-l border-gray-100 md:shadow-[-12px_0_16px_-14px_rgba(15,23,42,0.25)] text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-3 w-[300px] min-w-[300px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {pagedEntries.map((group) => {
                    const lead = group[0];
                    const count = group.length;
                    const name = capitalizeEachWord(lead.full_name || lead.name || 'Unknown');
                    const isOffer = lead.lead_type === 'offer';
                    const isInspection = lead.lead_type === 'inspection';
                    const isRenter = lead.lead_type === 'renter_enquiry';
                    const isApplication = lead.lead_type === 'agent_application';
                    const message = cleanEnquiryMessage(lead.initial_notes) || '';
                    const isExpanded = !!expandedMessages[lead.id];
                    const agentName = lead.agent_name || lead.agent || '';
                    const offerAmount = lead.price_range || '';

                    return (
                      <tr
                        key={lead.id}
                        className="hover:bg-[#f0fafb]/60 transition-colors group cursor-pointer"
                        onClick={() => openAssignModal(group)}
                      >
                        {/* Checkbox — omitted on the Offers tab (no bulk ignore). */}
                        {activeTab !== 'offers' && (
                          <td className="px-3 py-4 align-top" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={group.every((l) => selectedLeads.includes(l.id))}
                              onChange={() => toggleGroupSelection(group.map((l) => l.id))}
                              className="rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0]"
                            />
                          </td>
                        )}

                        {/* Received */}
                        <td className="px-3 py-4 align-top">
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-gray-500 font-medium whitespace-nowrap">
                              {moment(lead.created_at).format('ddd D MMM YYYY h:mm a')}
                            </span>
                          </div>
                        </td>

                        {/* Type Badge */}
                        <td className="px-3 py-4 align-top">
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide w-fit ${isOffer ? 'bg-emerald-100 text-emerald-700' : isInspection ? 'bg-indigo-100 text-indigo-700' : isRenter ? 'bg-amber-100 text-amber-700' : isApplication ? 'bg-purple-100 text-purple-700' : 'bg-red-100 text-red-700'}`}>
                              {isOffer ? 'Offer' : isInspection ? 'Inspection Request' : isRenter ? 'Renter Enquiry' : isApplication ? 'Agent Application' : (lead.source === 'form' ? 'Appraisal Lead' : 'Buyer Enquiry')}
                            </span>
                            {lead.is_processed && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide w-fit bg-blue-100 text-blue-700">
                                Processed
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Name */}
                        <td className="px-3 py-4 align-top">
                          <div className="flex items-center gap-2">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs ${isOffer ? 'bg-emerald-500' : isInspection ? 'bg-indigo-500' : isRenter ? 'bg-amber-500' : isApplication ? 'bg-purple-500' : 'bg-[#55C5D0]'}`}>
                              {getInitials(name)}
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <ContactLink
                                  identity={{
                                    contactId: lead.contact_id,
                                    buyerId: lead.buyer_id,
                                    email: lead.email,
                                    name,
                                    phone: lead.contact || lead.phone,
                                  }}
                                  className="font-semibold text-gray-900 text-xs text-left hover:text-[#3a99a3] hover:underline"
                                >
                                  {name}
                                </ContactLink>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Property. An applicant is not enquiring about a
                            listing, so this column would always read N/A -
                            show the recruitment ad they came off instead. */}
                        <td className="px-3 py-4 align-top">
                          <span className="text-xs text-gray-500 leading-relaxed truncate block max-w-[150px]">
                            {isApplication
                              ? (applicationCampaign(lead) || 'Recruitment ad')
                              : (lead.property_address || 'N/A')}
                          </span>
                        </td>

                        {/* Agents */}
                        <td className="px-3 py-4 align-top">
                          <span className="text-xs text-gray-700 font-medium">
                            {lead.agent_name || 'N/A'}
                          </span>
                        </td>

                        {/* Price */}
                        <td className="px-3 py-4 align-top text-center">
                          <span className="text-xs text-gray-950 font-semibold">
                            {lead.property_price || (formatMoney(lead.offer_amount || lead.offer_amount_min || offerAmount) || '—')}
                          </span>
                        </td>


                        {/* Source */}
                        <td className="px-3 py-4 align-top">
                          <span className="text-xs text-gray-500">{sourceLabel(lead.source)}</span>
                        </td>

                        {/* Status */}
                        <td className="px-3 py-4 align-top">
                          <span className="text-xs text-gray-500 font-medium italic">Pending/Unprocessed</span>
                        </td>

                        <td className="px-3 py-4 align-top min-w-[240px] w-[260px]">
                          {message ? (
                            <div>
                              <p className={`text-xs text-gray-700 leading-relaxed break-words ${isExpanded ? 'whitespace-pre-line' : 'line-clamp-3'}`}>
                                {message}
                              </p>
                              {message.length > 120 && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); toggleMessageExpand(lead.id); }}
                                  className="mt-1.5 text-xs text-[#3a99a3] font-semibold hover:underline inline-flex items-center gap-0.5"
                                >
                                  {isExpanded ? 'Show less' : 'Show more'}
                                  {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 italic">No message</span>
                          )}
                        </td>

                        {/* Actions - sticky so View/Ignore stay reachable even when the
                            table scrolls horizontally. White with a soft edge shadow
                            instead of the old grey block (Michael Piccoli, Monday
                            13283958708). */}
                        <td
                          className="md:sticky md:right-0 md:z-10 bg-white group-hover:bg-gray-50 border-l border-gray-100 md:shadow-[-12px_0_16px_-14px_rgba(15,23,42,0.25)] px-3 py-4 align-top w-[300px] min-w-[300px] transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="grid grid-cols-6 gap-1.5">
                            <button
                              onClick={(e) => { e.stopPropagation(); openAssignModal(group, false); }}
                              disabled={assigningLeadId === lead.id}
                              className={`${ROW_BTN} col-span-2 text-white bg-[#55C5D0] hover:bg-[#46acb6]`}
                            >
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>
                            {isOffer && activeTab !== 'ignored' ? (
                              // On the Offers tab the row carries the negotiation
                              // workflow instead of Add to Property / Database /
                              // Ignore (Christian Gravias, Monday 13099979503).
                              // The buttons are status-gated, so a pending offer
                              // shows Send to Vendor / Send Counter Offer / Decline
                              // / Fill Out Contract, matching the View pop-up.
                              <>
                                <NotesRowButton lead={lead} onOpen={setNotesLead} className="col-span-4" />
                                {lead.offer_id && (
                                  <div className="col-span-6 flex flex-wrap gap-1.5">
                                    <OfferWorkflowActions
                                      offer={{
                                        id: lead.offer_id,
                                        status: lead.offer_status,
                                        reference_number: lead.offer_reference,
                                        offer_amount: lead.offer_amount,
                                        offer_amount_min: lead.offer_amount_min,
                                        counter_amount: lead.counter_amount,
                                        deposit_amount: lead.deposit_amount,
                                        settlement_days: lead.settlement_days,
                                        custom_settlement_date: lead.custom_settlement_date,
                                        first_name: (name || '').split(' ')[0],
                                        last_name: (name || '').split(' ').slice(1).join(' '),
                                        buyer_name: name,
                                        buyer_email: lead.email,
                                        email: lead.email,
                                      }}
                                      propertyId={lead.property_id || lead.property}
                                      buyerName={name}
                                      buyerEmail={lead.email}
                                      size="sm"
                                      onOfferUpdated={() => invalidateCache.leads()}
                                      onDecline={() => handleOfferRowDeclined(lead)}
                                    />
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                {activeTab === 'ignored' ? (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleRestoreLead(group); }}
                                    disabled={restoringLeadId === lead.id}
                                    className={`${ROW_BTN} col-span-4 text-white bg-emerald-600 hover:bg-emerald-700`}
                                  >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                    {restoringLeadId === lead.id ? 'Restoring...' : 'Restore'}
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleAddToProperty(group); }}
                                    disabled={assigningLeadId === lead.id}
                                    title="Attach this enquiry to the property it came from. Once added it leaves the holding area."
                                    className={`${ROW_BTN} col-span-4 text-white bg-emerald-600 hover:bg-emerald-700`}
                                  >
                                    <Home className="w-3.5 h-3.5" /> Add to Property
                                  </button>
                                )}
                                {/* Was "Database" (add to a Database category). Christian
                                    Gravias, Monday 13283753306: this button is now the
                                    buyer's Buying Preferences. Full row of its own: the label
                                    is too long for a third of the 300px column (it ran
                                    into Notes, Christian 19 Oct). */}
                                <button
                                  onClick={(e) => { e.stopPropagation(); setPrefsLead(lead); }}
                                  title={lead.lead_type === 'renter_enquiry'
                                    ? "Open this renter's Renting Preferences"
                                    : "Open this buyer's Buying Preferences"}
                                  className={`${ROW_BTN_QUIET} col-span-6 text-indigo-700 border-indigo-200 hover:bg-indigo-50`}
                                >
                                  <SlidersHorizontal className="w-3.5 h-3.5" />
                                  {lead.lead_type === 'renter_enquiry' ? 'Renting Preferences' : 'Buying Preferences'}
                                </button>
                                {/* Notes + reminder series without opening the full View
                                    window (Christian Gravias, Monday 13166757620). */}
                                <NotesRowButton lead={lead} onOpen={setNotesLead} className="col-span-3" />
                                {activeTab === 'ignored' ? (
                                  canReassignIgnored && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setReassignLead(lead); }}
                                      className={`${ROW_BTN_QUIET} col-span-3 text-gray-700 border-gray-200 hover:bg-gray-50`}
                                    >
                                      <UserCheck className="w-3.5 h-3.5" /> Re-assign
                                    </button>
                                  )
                                ) : (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setIgnoreGroup(group); }}
                                    disabled={ignoringLeadId === lead.id}
                                    className={`${ROW_BTN_QUIET} col-span-3 text-orange-700 border-orange-200 hover:bg-orange-50`}
                                  >
                                    <EyeOff className="w-3.5 h-3.5" />
                                    {ignoringLeadId === lead.id ? '...' : 'Ignore'}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Page size + pagination (every tab, so no tab is a long scroll). */}
            {groupEntries.length > 0 && (
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-xs font-semibold text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25 bg-white"
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  <span>per page</span>
                  <span className="ml-1 text-gray-400">
                    {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, groupEntries.length)} of {groupEntries.length}
                  </span>
                </div>
                {uiPageCount > 1 && (
                  <ReactPaginate
                    previousLabel={"←"}
                    nextLabel={"→"}
                    breakLabel={"..."}
                    pageCount={uiPageCount}
                    marginPagesDisplayed={1}
                    pageRangeDisplayed={3}
                    onPageChange={(e) => setUiPage(e.selected + 1)}
                    forcePage={safePage - 1}
                    containerClassName={"pagination flex items-center space-x-2"}
                    activeClassName={"bg-[#55C5D0] text-white rounded"}
                    pageClassName={"rounded"}
                    pageLinkClassName={"block px-3 py-1 hover:bg-gray-200 rounded"}
                    previousClassName={"rounded"}
                    previousLinkClassName={
                      safePage === 1
                        ? "block px-3 py-1 bg-gray-200 text-gray-400 rounded cursor-not-allowed"
                        : "block px-3 py-1 bg-[#55C5D0] text-white rounded hover:bg-[#46acb6]"
                    }
                    nextClassName={"rounded"}
                    nextLinkClassName={
                      safePage === uiPageCount
                        ? "block px-3 py-1 bg-gray-200 text-gray-400 rounded cursor-not-allowed"
                        : "block px-3 py-1 bg-[#55C5D0] text-white rounded hover:bg-[#46acb6]"
                    }
                  />
                )}
              </div>
            )}
            </>
          );
        })()}
      </div>

      <LeadNotesModal
        isOpen={!!notesLead}
        lead={notesLead}
        onClose={() => setNotesLead(null)}
        onSaved={(saved) => {
          // Keep the row's note count in step without a reload.
          const patch = (l) => (l.id === saved.id ? { ...l, agent_notes: saved.agent_notes } : l);
          setLeadsList((prev) => prev.map(patch));
          setIgnoredList((prev) => prev.map(patch));
          setNotesLead((prev) => (prev && prev.id === saved.id ? { ...prev, agent_notes: saved.agent_notes } : prev));
          invalidateCache.leads();
        }}
      />

      <IgnoreLeadReasonModal
        kind="buyer"
        open={!!ignoreGroup}
        busy={!!ignoreGroup && ignoringLeadId === (Array.isArray(ignoreGroup) ? ignoreGroup[0]?.id : ignoreGroup?.id)}
        count={Array.isArray(ignoreGroup) ? ignoreGroup.length : 1}
        onClose={() => setIgnoreGroup(null)}
        onConfirm={(reason, notes) => {
          const group = ignoreGroup;
          setIgnoreGroup(null);
          if (group) handleIgnoreLead(group, reason, notes);
        }}
      />

      <IgnoreLeadReasonModal
        kind="buyer"
        open={bulkIgnoreOpen}
        busy={bulkIgnoring}
        count={selectedLeads.length}
        onClose={() => setBulkIgnoreOpen(false)}
        onConfirm={(reason, notes) => {
          setBulkIgnoreOpen(false);
          runBulkIgnore(false, reason, notes);
        }}
      />

      <ReassignIgnoredLeadModal
        open={!!reassignLead}
        lead={reassignLead}
        agents={(agents || []).map((a) => ({ id: a.id, name: a.name || a.email })).filter((a) => a.id && a.name)}
        onClose={() => setReassignLead(null)}
        onReassigned={(lead) => {
          // It's no longer ignored, so drop it from the Ignored tab.
          setIgnoredList((prev) => prev.filter((l) => l.id !== lead.id));
        }}
      />

      {/* Add to Property — lightweight picker (CG #12973323252). Attaches the
          enquiry straight to a chosen property without the full lead-preview
          modal, which only opens on View. */}
      {assignPropertyModal.open && assignPropertyModal.quick && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setAssignPropertyModal({ open: false, lead: null })}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900">Add to Property</h3>
              <button onClick={() => setAssignPropertyModal({ open: false, lead: null })}
                className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-500">
                Attach {assignPropertyModal.lead?.full_name || assignPropertyModal.lead?.name || 'this enquiry'}
                {(assignPropertyModal.group?.length || 1) > 1 ? ` and ${assignPropertyModal.group.length - 1} more` : ''} to a property.
                It leaves the holding area once added.
              </p>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Property</label>
                <select
                  value={assignPropertyId}
                  onChange={(e) => setAssignPropertyId(e.target.value)}
                  disabled={assignPropertiesLoading}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="">{assignPropertiesLoading ? 'Loading properties…' : 'Select a property…'}</option>
                  {assignProperties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.address || p.title || `Property #${p.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Message (optional)</label>
                <textarea
                  value={assignMessage}
                  onChange={(e) => setAssignMessage(e.target.value)}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Add a note to this enquiry (optional)"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setAssignPropertyModal({ open: false, lead: null })}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleAssignToProperty}
                disabled={!assignPropertyId || !!assigningLeadId}
                className="px-4 py-1.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50"
                style={{ background: '#55C5D0' }}>
                {assigningLeadId ? 'Adding…' : 'Add to Property'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign to Property Modal - powered by LeadPreviewModal (View) */}
      <LeadPreviewModal
        isOpen={assignPropertyModal.open && !assignPropertyModal.quick}
        onClose={() => setAssignPropertyModal({ open: false, lead: null })}
        lead={assignPropertyModal.lead}
        mode="assign"
        loading={!!assigningLeadId}
        getProperties={getProperties}
        getAgents={getAgents}
        currentUserName={user?.name || user?.email}
        fetchLeadReminders={getLeadReminders}
        bookLeadReminders={createLeadReminders}
        deleteLeadReminder={deleteLeadReminder}
        onOpenPreferences={(l) => setPrefsLead(l)}
        // Opens the "why ignore?" modal rather than ignoring straight away, so
        // the View pop-up can't skip the reason (Monday 13225628427).
        onIgnore={(l) => setIgnoreGroup(assignPropertyModal.group || l)}
        onDatabaseSaved={(l) => handleDatabaseSaved(l)}
        onDecline={(l) => {
          // Offer already marked Declined on the property by the modal. Drop the
          // lead(s) from the Buyer Portal Offers holding area and close. No toast
          // here — the modal already confirmed the decline.
          const group = assignPropertyModal.group || [l];
          const ids = new Set(group.map((g) => g.id));
          setLeadsList((prev) => prev.filter((x) => !ids.has(x.id)));
          // Marked, not deleted, so the buyer stays in the CRM and the declined
          // Offer stays on the property's Offers tab.
          Promise.all(group.map((g) => ignoreLead(g.id, { reason: 'offer_declined' })))
            .then(() => invalidateCache.leads())
            .catch(() => { });
          setAssignPropertyModal({ open: false, lead: null });
        }}
        onAddNote={async (newNote) => {
          const lead = assignPropertyModal.lead;
          if (!lead?.id) return;
          const timestamp = moment().format('D/M/YYYY h:mm a');
          const authorName = user?.name || user?.email || 'Agent';
          const newNoteEntry = {
            timestamp,
            author: authorName,
            content: newNote
          };
          // Only the new note goes up. Sending the whole array from this row's
          // copy of agent_notes deleted anything written since the row was
          // loaded (Christian Gravias, 24 Aug 2026).
          const res = await updateLead(lead.id, { append_agent_note: newNoteEntry });
          const updatedNotes = Array.isArray(res?.data?.agent_notes)
            ? res.data.agent_notes
            : [...(Array.isArray(lead.agent_notes) ? lead.agent_notes : []), newNoteEntry];
          invalidateCache.leads();
          toast.success("Note added!");
          setAssignPropertyModal(prev => ({ ...prev, lead: { ...prev.lead, agent_notes: updatedNotes } }));
        }}
        onAssign={async (propertyId, message, leadForm, offerForm) => {
          const lead = assignPropertyModal.lead;
          const group = assignPropertyModal.group || [lead];
          if (!lead || !propertyId) {
            toast.error('Please select a property.');
            return;
          }
          setAssigningLeadId(lead.id);
          try {
            // 1. Save all the qualification details for all members of the group
            const isOffer = lead.lead_type === 'offer' || lead.intent === 'offer' || (lead.tags && lead.tags.includes('offer'));
            await Promise.all(group.map(l =>
              updateLead(l.id, {
                full_name: leadForm.name,
                phone: leadForm.phone,
                agent: leadForm.agent,
                source: leadForm.source,
                priority: leadForm.priority,
                buyer_property_type: leadForm.type,
                intent: leadForm.intent,
                buyer_budget_min: leadForm.budget_min ? parseInt(String(leadForm.budget_min).replace(/[^0-9]/g, '')) : null,
                buyer_budget_max: leadForm.budget_max ? parseInt(String(leadForm.budget_max).replace(/[^0-9]/g, '')) : null,
                timeline: leadForm.timeline,
                commission: leadForm.commission,
                gained_commission: leadForm.gained_commission ? parseInt(String(leadForm.gained_commission).replace(/[^0-9]/g, '')) : null,
                settlement_date: leadForm.settlement_date,
                // Include offer details if this is an offer (Lead model fields)
                ...(isOffer && offerForm ? {
                  offer_amount_min: offerForm.offer_amount ? parseInt(String(offerForm.offer_amount).replace(/[^0-9]/g, '')) : null,
                  offer_amount_max: offerForm.offer_amount ? parseInt(String(offerForm.offer_amount).replace(/[^0-9]/g, '')) : null,
                  deposit_amount: offerForm.deposit_amount ? parseInt(String(offerForm.deposit_amount).replace(/[^0-9]/g, '')) : null,
                  settlement_days: offerForm.settlement_days ? parseInt(offerForm.settlement_days) : null,
                  custom_settlement_date: offerForm.custom_settlement_date || null,
                } : {})
              })
            ));

            // 2. Perform the assignment for all members
            await Promise.all(group.map(l =>
              addLeadToPropertyEnquiry(l.id, parseInt(propertyId), message)
            ));

            invalidateCache.leads();
            toast.success(lead.lead_type === 'offer' ? 'Offer(s) assigned to property!' : 'Enquiry(s) assigned to property!');

            // Remove only the specific leads that were assigned (by ID), not all with same email/phone
            const assignedIds = new Set(group.map(gl => gl.id));
            setLeadsList((prev) => prev.filter((l) => !assignedIds.has(l.id)));

            setAssignPropertyModal({ open: false, lead: null });
          } catch (err) {
            toast.error(err?.response?.data?.error || err?.response?.data?.detail || 'Failed to assign.');
          } finally {
            setAssigningLeadId(null);
          }
        }}
      />

      {prefsLead && (
        prefsLead.lead_type === 'renter_enquiry' ? (
          <RentingPreferencesModal
            identity={{
              contactId: prefsLead.contact_id,
              email: prefsLead.email,
              name: prefsLead.full_name || prefsLead.name,
              phone: prefsLead.contact || prefsLead.phone,
            }}
            title={prefsLead.full_name || prefsLead.name}
            onClose={() => setPrefsLead(null)}
          />
        ) : (
          <BuyerPreferencesModal
            identity={{
              contactId: prefsLead.contact_id,
              email: prefsLead.email,
              name: prefsLead.full_name || prefsLead.name,
              phone: prefsLead.contact || prefsLead.phone,
            }}
            title={prefsLead.full_name || prefsLead.name}
            onClose={() => setPrefsLead(null)}
          />
        )
      )}

      {/* Edit Lead Modal - kept for view details */}
      {editModalOpen && (
        <AddLeadModal
          isAdmin={isAdmin}
          isOpen={editModalOpen}
          agents={agents}
          propertySubmit={(data) => {
            setProperty(data);
          }}
          onClose={() => {
            setEditModalOpen(false);
            setEditLead(null);
          }}
          initialValues={editLead}
          loading={updating}
          onSubmit={async (data) => {
            setUpdating(true);
            try {
              const payload = {
                name: data.full_name,
                email: data.email,
                phone: data.contact,
                property_address: data.property_address,
                source: data.source,
                stage: data.stage,
                priority: data.priority,
                timeline: data.timeline,
                initial_notes: data.initial_notes,
                agent: data.agent,
                buyer_budget_min: data.min_price ? parseInt(data.min_price) : null,
                buyer_budget_max: data.max_price ? parseInt(data.max_price) : null,
                commission: data.commission,
                gained_commission: data.gained_commission,
                settlement_date: data.settlement_date,
              };

              if (data.price_range && (!payload.buyer_budget_min && !payload.buyer_budget_max)) {
                const parts = data.price_range.split('-');
                if (parts[0]) payload.buyer_budget_min = parseInt(parts[0].replace(/[^0-9]/g, ''));
                if (parts[1]) payload.buyer_budget_max = parseInt(parts[1].replace(/[^0-9]/g, ''));
              }
              let updateResponse;
              try {
                updateResponse = await updateLead(editLead.id, payload);
              } catch (updateError) {
                if (updateError?.response?.status >= 200 && updateError?.response?.status < 300) {
                  updateResponse = updateError.response;
                } else {
                  throw updateError;
                }
              }

              invalidateCache.leads();

              if (updateResponse?.data?.detail && updateResponse.data.detail.includes("successfully")) {
                toast.success(updateResponse.data.detail);
              } else {
                toast.success("Lead updated successfully!");
              }

              setEditModalOpen(false);
              setEditLead(null);
              fetchLeads();
            } catch (error) {
              if (error?.response?.data?.detail && error.response.data.detail.includes("successfully")) {
                toast.success(error.response.data.detail);
                setEditModalOpen(false);
                setEditLead(null);
                fetchLeads();
              } else {
                toast.error(
                  error?.response?.data?.detail
                );
              }
            } finally {
              setUpdating(false);
            }
          }}
          isEdit={true}
          isUpdating={updating}
          onAddNote={async (notes) => {
            if (!editLead?.id) return;
            await updateLead(editLead.id, { initial_notes: notes });
            invalidateCache.leads();
            toast.success("Note added successfully!");
            setEditLead((prev) => (prev ? { ...prev, initial_notes: notes } : null));
          }}
          onAddToPropertyEnquiry={async (leadId, propertyId, message) => {
            await addLeadToPropertyEnquiry(leadId, propertyId, message);
            invalidateCache.leads();
            toast.success("Buyer enquiry added to property enquiries!");
            setEditModalOpen(false);
            setEditLead(null);
            // Remove from list
            setLeadsList((prev) => prev.filter((l) => l.id !== leadId));
          }}
          getProperties={getProperties}
        />
      )}

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={isAddModalOpen}
        loading={loading}
        agents={agents}
        propertySubmit={(data) => {
          setProperty(data);
        }}
        isAdmin={isAdmin}
        onClose={() => setIsAddModalOpen(false)}
        onSave={async (data) => {
          await handleAddLead(data);
          fetchLeads();
          setIsAddModalOpen(false);
        }}
      />
    </div>
  );
};

export default LeadManagement;

export const AddLeadModal = ({
  isOpen,
  onClose,
  onSubmit,
  onSave,
  initialValues = {},
  isEdit,
  loading = false,
  isAdmin = false,
  propertySubmit,
  onAddNote,
  onAddToPropertyEnquiry,
  getProperties,
}) => {
  const submitHandler = onSubmit || onSave;
  // Helper function to convert formatted phone to E.164 format
  const parsePhoneToE164 = (phone) => {
    if (!phone) return "";
    // Remove all characters except + and digits
    const cleaned = phone.replace(/[^\d+]/g, "");
    return cleaned;
  };

  const [agents, setAgents] = useState([]);
  const [addModal, setAddModal] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState(
    initialValues?.property?.id ?? initialValues?.property ?? ""
  );
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [addNoteLoading, setAddNoteLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [property, setProperty] = useState(null);
  const [formData, setFormData] = useState({
    firstName: initialValues?.full_name?.split(" ")[0] || "",
    lastName: initialValues?.full_name?.split(" ").slice(1).join(" ") || "",
    fullName: initialValues?.full_name || "",
    phoneNumber: parsePhoneToE164(initialValues?.contact) || "",
    email: initialValues?.email || "",
    propertyAddress: initialValues?.property_address || "",
    leadSource: initialValues?.source || "",
    timeline: initialValues?.timeline || "",
    priority: initialValues?.priority || "Medium",
    stage: initialValues?.stage || "Unassessed",
    initialNotes: cleanEnquiryMessage(initialValues?.initial_notes) || "",
    agentId: initialValues?.agent || "",
    agent: initialValues?.agent || "",
    minPrice: initialValues?.buyer_budget_min || initialValues?.price_range?.split("-")[0]?.trim() || "",
    maxPrice: initialValues?.buyer_budget_max || initialValues?.price_range?.split("-")[1]?.trim() || "",
    commission: initialValues?.commission || "",
    gainedCommission: initialValues?.gained_commission || "",
    settlementDate: initialValues?.settlement_date || "",
    propertyType: initialValues?.buyer_property_type || "",
    intent: initialValues?.intent || "",
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (isEdit) return;
    setFormData({
      firstName: "",
      lastName: "",
      fullName: "",
      phoneNumber: "",
      email: "",
      propertyAddress: "",
      leadSource: "",
      timeline: "",
      priority: "Medium",
      stage: "New Lead",
      initialNotes: "",
      agentId: "",
      agent: "",
      commission: "",
      gainedCommission: "",
      settlementDate: "",
      propertyType: "",
      intent: "",
    });
    setFormErrors({});
  }, [isEdit]); // Remove isOpen dependency to prevent form reset on modal open/close
  // console.log({ agents });
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    if (initialValues?.stage === "Won - Listing Signed") {
      setAddModal(false);
    } else if (formData?.stage === "Won - Listing Signed") {
      setAddModal(true);
    }
  }, [formData?.stage]);
  // Fetch agents from API
  const fetchAgents = async () => {
    try {
      const res = await getAgents();
      // console.log({ res });
      // Handle both paginated and non-paginated responses
      const agentsData = res.data?.results || res.data || [];
      setAgents(agentsData);
    } catch (error) {
      console.error("Failed to fetch agents:", error);
      toast.error("Failed to fetch agents.");
    }
  };

  useEffect(() => {
    if (isAdmin) fetchAgents();
  }, []);

  useEffect(() => {
    setSelectedPropertyId(initialValues?.property?.id ?? initialValues?.property ?? "");
  }, [initialValues?.property]);

  useEffect(() => {
    if (isEdit && getProperties) {
      setPropertiesLoading(true);
      // agency_wide: attach an enquiry to any property, not only the caller's own.
      getProperties({ page_size: 50, agency_wide: true })
        .then((res) => {
          const list = res?.data?.results || res?.data || [];
          setProperties(Array.isArray(list) ? list : []);
        })
        .catch(() => setProperties([]))
        .finally(() => setPropertiesLoading(false));
    }
  }, [isEdit, getProperties]);

  const handleAddNote = async () => {
    if (!onAddNote || !formData.initialNotes?.trim()) return;
    setAddNoteLoading(true);
    try {
      await onAddNote(formData.initialNotes.trim());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add note");
    } finally {
      setAddNoteLoading(false);
    }
  };

  useEffect(() => {
    if (initialValues?.property_item) {
      getSpecificCrawlerProperty(initialValues?.property_item).then((res) => {
        console.log(res);
        setProperty(res.data);
      });
    }
  }, [initialValues?.property_item]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // Email validation helper
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = {};
    const fullName = `${formData.firstName} ${formData.lastName}`.trim();
    if (!fullName) errors.firstName = "Name is required.";

    // Improved phone number validation with better error handling
    if (!formData.phoneNumber) {
      errors.phoneNumber = "Phone number is required.";
    } else {
      // Normalize phone number - remove any whitespace
      const normalizedPhone = formData.phoneNumber.trim();

      if (!normalizedPhone) {
        errors.phoneNumber = "Phone number is required.";
      } else {
        // Extract digits to check minimum length
        const digitsOnly = normalizedPhone.replace(/\D/g, '');

        // Basic length check - phone numbers should have at least 7 digits
        if (digitsOnly.length < 7) {
          errors.phoneNumber = "Phone number is too short. Please enter a valid phone number.";
        }
        // If it has 7+ digits, allow it (PhoneInput component handles formatting)
        // No need for overly strict validation - backend will validate if needed
      }
    }

    if (!formData.email) errors.email = "Email is required.";
    else if (!isValidEmail(formData.email))
      errors.email = "Invalid email address.";
    if (!formData.propertyAddress.trim())
      errors.propertyAddress = "Property address is required.";
    if (!formData.leadSource) errors.leadSource = "Lead source is required.";
    if (!formData.priority) errors.priority = "Priority is required.";
    if (!formData.stage) errors.stage = "Status is required.";
    if (isAdmin && !(isEdit ? formData.agent : formData.agentId))
      errors.agentId = "Assigned agent is required.";
    const minPrice = formData.minPrice || "";
    const maxPrice = formData.maxPrice || "";
    if (minPrice && maxPrice && Number(minPrice) > Number(maxPrice)) {
      errors.minPrice = "Min price cannot be greater than Max price.";
      errors.maxPrice = "Min price cannot be greater than Max price.";
    }
    if (formData.commission && (Number(formData.commission) < 0 || Number(formData.commission) > 100)) {
      errors.commission = "Commission must be between 0 and 100.";
    }
    if (formData.gainedCommission && (Number(formData.gainedCommission) < 0 || Number(formData.gainedCommission) > 100)) {
      errors.gainedCommission = "Gained commission must be between 0 and 100.";
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    if (onAddToPropertyEnquiry && isEdit && !selectedPropertyId) {
      toast.error("Please select a property to add this enquiry to.");
      return;
    }

    const priceRangeString =
      minPrice && maxPrice
        ? `${minPrice} - ${maxPrice}`
        : minPrice
          ? `${minPrice}`
          : maxPrice
            ? `${maxPrice}`
            : "";

    // Normalize phone number for submission - ensure proper E.164 format.
    // Shared with the Owner Information field so a number typed as
    // "0412 345 678", "(04) 1234 5678" or "61412345678" all store the same way.
    const typedPhoneNumber = formData.phoneNumber?.trim() || "";
    const normalizedPhoneNumber =
      normalizePhoneInput(typedPhoneNumber) || typedPhoneNumber;

    const payload = {
      full_name: `${formData.firstName} ${formData.lastName}`.trim(),
      contact: normalizedPhoneNumber,
      email: formData.email,
      property_address: formData.propertyAddress,
      source: formData.leadSource,
      stage: formData.stage,
      priority: formData.priority,
      timeline: formData.timeline || "",
      initial_notes: formData.initialNotes || "",
      agent: isEdit ? formData.agent : formData.agentId,
      min_price: formData.minPrice,
      max_price: formData.maxPrice,
      price_range: priceRangeString,
      buyer_property_type: formData.propertyType,
      intent: formData.intent,
    };

    // Only add numeric/date fields if they have valid values
    if (formData.commission && formData.commission.trim() !== "") {
      payload.commission = formData.commission;
    }
    if (formData.gainedCommission && formData.gainedCommission.trim() !== "") {
      payload.gained_commission = formData.gainedCommission;
    }
    if (formData.settlementDate && formData.settlementDate.trim() !== "") {
      payload.settlement_date = formData.settlementDate;
    }

    (async () => {
      await submitHandler(payload);
      if (onAddToPropertyEnquiry && selectedPropertyId && initialValues?.id) {
        try {
          await onAddToPropertyEnquiry(initialValues.id, selectedPropertyId, formData.initialNotes);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Lead saved but failed to add to property enquiries");
        }
      }
    })();
  };

  if (!isOpen) return null;

  // Section icons
  const sectionIcons = {
    contact: <User className="w-5 h-5 text-[#55C5D0] mr-2" />,
    property: <MapPin className="w-5 h-5 text-[#55C5D0] mr-2" />,
    assignment: <Users className="w-5 h-5 text-[#55C5D0] mr-2" />,
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden animate-[modalIn_0.3s_ease]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b bg-white">
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
            {isEdit ? "View Buyer Enquiry" : "Add New Lead"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        <form className="flex-1 overflow-y-auto p-8 space-y-8" onSubmit={handleSubmit}>
          {/* Section: Contact Details */}
          <section className="space-y-4">
            <div className="bg-slate-50/80 py-2 px-4 rounded-lg">
              <h3 className="text-sm font-bold text-gray-800 tracking-tight">Contact Details</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">First Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all"
                  placeholder="First Name"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">Last Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all"
                  placeholder="Last Name"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all"
                  placeholder="Email"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">Phone <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange({ target: { name: 'phoneNumber', value: e.target.value } })}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all"
                  placeholder="Phone"
                />
              </div>
            </div>
          </section>

          {/* Section: Enquiry Details */}
          <section className="space-y-4">
            <div className="bg-slate-50/80 py-2 px-4 rounded-lg">
              <h3 className="text-sm font-bold text-gray-800 tracking-tight">Enquiry Details</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">Lead Type</label>
                <select
                  name="leadType"
                  value={formData.leadType || 'buyer'}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all appearance-none"
                >
                  <option value="buyer">Buyer enquiry</option>
                  <option value="owner">Owner enquiry</option>
                  <option value="general">General enquiry</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">Source</label>
                <select
                  name="leadSource"
                  value={formData.leadSource}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all appearance-none"
                >
                  <option value="buyer_portal">CRE</option>
                  <option value="rea">Realestate.com.au</option>
                  <option value="domain">Domain.com.au</option>
                  <option value="direct">Direct</option>
                  <option value="chatbot">Chatbot</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">Selling Time Frame</label>
                <select
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all appearance-none"
                >
                  <option value="">Not specified</option>
                  {mergeTimeframeOptions(formData.timeline).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">Received</label>
                <div className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-gray-600 font-medium">
                  {initialValues?.created_at ? moment(initialValues.created_at).format("ddd D MMM, h:mma") : moment().format("ddd D MMM, h:mma")}
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">Assigned Agent</label>
                <select
                  name={isEdit ? "agent" : "agentId"}
                  value={isEdit ? formData.agent : formData.agentId}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all appearance-none"
                >
                  <option value="">Select Agent</option>
                  {agents?.map(agent => (
                    <option key={agent.id} value={agent.id}>{agent.name}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 space-y-1">
                <label className="text-xs font-semibold text-gray-500">Status</label>
                <select
                  name="stage"
                  value={formData.stage}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all appearance-none"
                >
                  <option value="Unassessed">Unassessed</option>
                  <option value="New Lead">New Lead</option>
                  <option value="Attempting Contact">Attempting Contact</option>
                  <option value="Follow-Up Ongoing">Follow-Up Ongoing</option>
                </select>
              </div>
            </div>
          </section>

          {/* Section: Property Information */}
          <section className="space-y-4">
            <div className="bg-slate-50/80 py-2 px-4 rounded-lg">
              <h3 className="text-sm font-bold text-gray-800 tracking-tight">Property Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">Property Address</label>
                <input
                  type="text"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all"
                  placeholder="e.g. 123 Main St"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">Property Type</label>
                <input
                  type="text"
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all"
                  placeholder="e.g. Apartment, House"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">Intent</label>
                <input
                  type="text"
                  name="intent"
                  value={formData.intent}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all"
                  placeholder="e.g. Buying, Investing"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">Price Expectation</label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number"
                    name="minPrice"
                    value={formData.minPrice}
                    onChange={handleInputChange}
                    className="w-full px-4 py-1.5 bg-white border border-gray-200 rounded-lg text-sm"
                    placeholder="Min"
                  />
                  <input
                    type="number"
                    name="maxPrice"
                    value={formData.maxPrice}
                    onChange={handleInputChange}
                    className="w-full px-4 py-1.5 bg-white border border-gray-200 rounded-lg text-sm"
                    placeholder="Max"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Property selector for Add to Property Enquiries */}
          {isEdit && onAddToPropertyEnquiry && (
            <section className="space-y-4">
              <div className="bg-slate-50/80 py-2 px-4 rounded-lg">
                <h3 className="text-sm font-bold text-gray-800 tracking-tight">Add to Property Enquiries</h3>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">Select property to add this enquiry to</label>
                <select
                  value={selectedPropertyId}
                  onChange={(e) => setSelectedPropertyId(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all appearance-none"
                >
                  <option value="">Select a property...</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title || p.address || `Property ${p.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </section>
          )}

          {/* Section: Original Enquiry Message */}
          <section className="space-y-4">
            <div className="bg-slate-50/80 py-2 px-4 rounded-lg">
              <h3 className="text-sm font-bold text-gray-800 tracking-tight">Original Enquiry Message</h3>
            </div>
            <div className="space-y-4">
              <textarea
                name="initialNotes"
                value={formData.initialNotes}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-[#55C5D0]/20 focus:border-[#55C5D0] transition-all resize-none text-gray-700"
                placeholder="The original message sent by the buyer..."
              />
              <button
                type="button"
                onClick={handleAddNote}
                disabled={!onAddNote || !formData.initialNotes?.trim() || addNoteLoading}
                className="flex items-center gap-2 px-4 py-2 bg-[#55C5D0] text-white rounded-lg font-bold shadow-sm hover:shadow-md hover:bg-[#4ab4be] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={18} />
                {addNoteLoading ? "Saving..." : "Add Note"}
              </button>
            </div>
          </section>
        </form>

        {/* Modal Footer - Premium Styled Buttons */}
        <div className="px-8 py-6 border-t bg-white flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 bg-white border border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50 transition-all active:scale-95"
          >
            Ignore Enquiry
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center gap-2 px-8 py-2.5 bg-[#55C5D0] text-white rounded-xl font-bold shadow-lg shadow-[#55C5D0]/20 hover:shadow-xl hover:bg-[#4ab4be] transition-all active:scale-95 disabled:opacity-50"
          >
            {loading ? "Processing..." : (
              <>
                Add to Property Enquiries
              </>
            )}
          </button>
        </div>
        {formData.stage === "Won - Listing Signed" && (
          <AnimatePresence>
            {addModal && (
              <motion.div
                key="add-property-modal"
                initial={{ x: "100%", opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "100%", opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="fixed inset-0 z-50 shadow-lg overflow-auto"
              >
                <AddPropertyForm
                  lead_id={initialValues?.id}
                  isCrossIconPress={() => {
                    handleInputChange({
                      target: { name: "stage", value: "New Lead" },
                    });
                    setAddModal(false);
                  }}
                  isEdit={property ? true : false}
                  initialValues={property}
                  isModal
                  onClose={() => setAddModal(false)}
                  isSuccess={() => setIsSuccess(true)}
                  onSubmit={(data) => {
                    setAddModal(false);
                    propertySubmit({ ...data, lead_id: initialValues?.id });
                    setProperty({
                      ...data,
                      title: data?.title,
                      address: data?.address,
                      city: data?.city || "",
                      state: data?.state || "",
                      postal_code: data?.postal_code || "",
                      price: data?.price,
                      beds: data?.beds,
                      baths: data?.baths,
                      parkings: data?.parking,
                      area: data?.area,
                      status: data?.status,
                      description: data?.description,
                      features: data.features,
                      email: data?.owner?.email || "",
                      property_type: data?.propertyTypes,
                      owner_visibilty: data?.publicShare,
                      owner: {
                        contact: data.contact,
                        email: data.email,
                        name: data.agent,
                      },
                    });
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

/* Helper Components */
const NotesRowButton = ({ lead, onOpen, className = "" }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onOpen(lead); }}
    title="Notes and call-back reminders for this enquiry"
    className={`${ROW_BTN_QUIET} text-gray-700 border-gray-200 hover:bg-gray-50 ${className}`}
  >
    <StickyNote className="w-3.5 h-3.5" />
    Notes{Array.isArray(lead.agent_notes) && lead.agent_notes.length ? ` (${lead.agent_notes.length})` : ''}
  </button>
);

const DetailRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-3">
    {icon}
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="font-medium text-gray-800">{value}</p>
    </div>
  </div>
);

const Avatar = ({ label, bg }) => (
  <div
    className={`w-8 h-8 rounded-full ${bg} text-white flex items-center justify-center font-bold`}
  >
    {label}
  </div>
);
